import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useCompany } from '@/contexts/useCompany';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { supabase } from '@/integrations/supabase/client';
import { QuoteForm, QuoteFormData, QuoteItem, calculateTotal, GeneralPhoto } from '@/components/quotes/QuoteForm';
import { QuoteActionBar, ActionSpinner } from '@/components/quotes/QuoteActionBar';
import {
  buildQuoteTotalsColumns,
  computeQuoteTotals,
  quoteHeaderFromRow,
  quoteItemLineTotal,
} from '@/lib/quoteTotals';
import { buildQuotePaymentColumns, quotePaymentInfoFromRow } from '@/lib/quotePaymentInfo';
import { buildQuotePdfTemplateColumns, quotePdfTemplateFromRow, resolveQuotePdfTemplate } from '@/lib/quotePdfTemplate';
import { buildQuoteWarrantyColumns, quoteWarrantyFromRow, warrantyDisplayText } from '@/lib/quoteWarranty';
import { buildQuoteHeadingColumns, quoteHeadingFromRow } from '@/lib/quoteHeading';
import { buildQuoteExtraFieldsColumns, quoteExtraFieldsFromRow } from '@/lib/quoteExtraFields';
import { QuoteApprovalSignature } from '@/components/quotes/QuoteApprovalSignature';
import type { Quote, QuoteItem as QuoteItemType, Client, QuotePhoto } from '@/types/database';
import { ArrowLeft, Trash2, FileDown, Loader2, Save, Copy, Link, Eye, AlertTriangle, WifiOff, ReceiptText, FileSignature, ClipboardList, ChevronRight, Info, Hash, type LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { downloadQuotePDF, PDFQuoteData } from '@/lib/pdfGenerator';
import { ThermalPrintButton } from '@/components/pdf/ThermalPrintButton';
import { isThermalFormat, thermalSpec, type PaperFormat } from '@/lib/thermalPaper';
import { onlyDigits } from '@/lib/documentUtils';
import { buildNewClientColumns, resolveClientAddressLine } from '@/lib/clientAddress';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import { getPublicQuoteUrl } from '@/lib/urls';
import { SendMessageDialog } from '@/components/messages/SendMessageDialog';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { buildQuoteMessageContext } from '@/lib/messageTemplates';
import { ensureStorageUrl } from '@/lib/offlineSync/storageUpload';
import { getCachedQuotes, getCachedClients, getCachedCompanies, getCachedCatalog, cacheQuotes, cacheClients, cacheCatalog, stripCachedCatalogMedia } from '@/lib/offlineDataCache';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { QuoteFinancePrompt } from '@/components/finance/QuoteFinancePrompt';
import { QuotePixChargeCard } from '@/components/payments/QuotePixChargeCard';
import { usePaymentDetection } from '@/hooks/usePaymentDetection';
import { useQuoteCharge } from '@/hooks/useQuoteCharge';
import { QuoteBillableExpenses } from '@/components/finance/QuoteBillableExpenses';
import { BILLABLE_EXPENSES_UPDATED_EVENT } from '@/lib/billableExpense';
import { GenerateReceiptButton } from '@/components/receipts/GenerateReceiptButton';
import { StockDeductionPrompt } from '@/components/stock/StockDeductionPrompt';
import { StockReturnPrompt } from '@/components/stock/StockReturnPrompt';
import { StockAdjustmentPrompt } from '@/components/stock/StockAdjustmentPrompt';
import { StockReturnOnDeleteNotice } from '@/components/stock/StockReturnOnDeleteNotice';
import { useDocumentDeletionReturn } from '@/hooks/useDocumentDeletionReturn';
import { runDocumentDeletion, type DocumentDeletionOutcome } from '@/lib/documentDeletion';
import { billableReleaseMessage, deleteQuotesCascade, type QuoteDeletionCounts } from '@/lib/quoteDeletion';
import { plural } from '@/lib/plural';
import { useWork } from '@/hooks/useWork';
import { WorkTrail, type WorkTrailAction } from '@/components/work/WorkTrail';
import { WorkGroupSection } from '@/components/work/WorkGroupSection';
import type { ReceiptRouteState } from '@/pages/ReceiptFormPage';
import { addToOfflineQueue, getOfflineQueue, removeFromOfflineQueue, handleOfflineMutationOfPendingRecord } from '@/lib/offlineStorage';
import {
  buildQuoteItemInsert,
  buildQuoteItemRow,
  catalogProductColumnsFromQuoteItem,
  quoteItemUnitCost,
} from '@/lib/quoteItems';
import { createCatalogProductFromQuoteItem } from '@/lib/catalogFromQuoteItem';
import { QUOTE_STATUS_LABELS, QUOTE_STATUS_OPTIONS } from '@/lib/quoteStatus';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { releaseSavedUploads } from '@/lib/pendingUploads';
import { documentTypeOfLabel, getDocumentTypeShortLabel } from '@/lib/documentTypeLabels';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { todayIsoDate } from '@/lib/financeStatus';

// Key for edit draft in localStorage - uses quote ID for uniqueness
const getEditDraftKey = (quoteId: string) => `orcazap_draft_edit_${quoteId}`;

function normalizeItemSource(raw: any): 'manual' | 'catalog' {
  // An item is from catalog if:
  // 1. It has source='catalog' explicitly set
  // 2. It has a product_id (real UUID or offline temporary ID)
  // 3. It was marked with saveToCategory=true (item was saved to catalog during quote creation)
  if (raw?.source === 'catalog') return 'catalog';
  
  // Check for product_id presence - this covers BOTH:
  // - Real UUIDs from online-saved items
  // - Temporary IDs with 'offline_' prefix from offline-saved items
  const productId = raw?.product_id;
  if (productId != null && productId !== '' && productId !== undefined) {
    return 'catalog';
  }
  
  // Check if item was marked to be saved to catalog (during offline creation)
  if (raw?.saveToCategory === true) return 'catalog';
  
  return 'manual';
}

export default function QuoteDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, profile, loading: authLoading } = useAuth();
  const { incrementQuoteCount, canCreateQuote, remainingQuotes, freeQuotesLimit } = useSubscription();
  const unlimitedQuotes = useFeature('unlimited_quotes');
  const noBrandingFooter = useFeature('no_branding_footer');
  const nfseFeature = useFeature('nfse_emission');
  const contractsFeature = useFeature('contracts');
  // Team roles: members+ may edit/duplicate; only owner/admin may delete;
  // only owner/admin see the finance "register payment" prompt.
  const { canWrite, canDelete, canSeeFinance } = useRole();
  const { activeCompany, companies } = useCompany();
  const { isOnline } = useNetworkStatus();
  // QR estático × dinâmico. Os dois alimentam o payload do PDF; o card da
  // cobrança consome os mesmos hooks (as queries são compartilhadas pelo cache
  // do TanStack, então não há requisição a mais).
  const paymentDetection = usePaymentDetection();
  const quoteCharge = useQuoteCharge(id);

  // "Próximos passos" — derive a Service Order / Contract from this quote, opening
  // the same form modals pre-seeded (no re-picking). Traceability card lists what
  // this quote already became.
  // O recibo passou a ser uma ação uniforme da trilha (antes vinha do
  // `GenerateReceiptButton`, que traz o próprio botão verde e destoaria da lista
  // recolhida), então esta tela passa a ser dona do modal.
  const [sendMessageOpen, setSendMessageOpen] = useState(false);
  // O trabalho a que este orçamento pertence: a trilha de etapas substitui a
  // antiga lista plana "Documentos gerados", que dizia o que o orçamento virou
  // mas não em que pé o serviço está.
  const { work, roots: workRoots, refetch: refetchWork } = useWork(
    id ? { kind: 'quote', id } : null,
  );

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // A devolução de estoque na exclusão só é oferecível ENQUANTO o documento
  // existe (`stock_movements.quote_id` é SET NULL) — por isso a decisão mora no
  // diálogo de confirmação, e a consulta só roda com ele aberto.
  const deletionReturn = useDocumentDeletionReturn('quote', id ? [id] : [], {
    enabled: deleteOpen,
  });
  const [duplicating, setDuplicating] = useState(false);
  const [quoteDate, setQuoteDate] = useState(todayIsoDate());
  
  // Duplicate confirmation modal state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  
  // Client change confirmation modal state
  const [showClientChangeModal, setShowClientChangeModal] = useState(false);
  const [pendingClientChangeCallback, setPendingClientChangeCallback] = useState<(() => void) | null>(null);
  
  // Track the original client ID from the database to detect changes
  const [originalClientId, setOriginalClientId] = useState<string | null>(null);
  /**
   * Os itens como estão GRAVADOS, não como estão sendo digitados.
   *
   * O acerto de estoque por quantidade alterada compara movimento × documento —
   * e o documento aqui é o do banco. Usar `formData.items` faria o app acertar
   * o estoque a partir de um número que o usuário ainda está digitando (e pode
   * abandonar), o que no modo automático mexeria no saldo sem save nenhum.
   */
  const [persistedItems, setPersistedItems] = useState<QuoteItem[]>([]);
  /** `updated_at` gravado — o outro lado da comparação com os movimentos. */
  const [persistedUpdatedAt, setPersistedUpdatedAt] = useState<string | null>(null);
  
  // Store the company associated with this quote (from DB, not the active company)
  const [quoteCompany, setQuoteCompany] = useState<typeof activeCompany>(null);
  
  // Store initial form data to detect changes (dirty state)
  const initialFormDataRef = useRef<string>('');
  const [isFormDirty, setIsFormDirty] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Track last fetch "mode" so we can refetch when coming back online (critical for offline restart)
  const fetchAttemptedRef = useRef<string | null>(null);
  
  const [formData, setFormDataInternal] = useState<QuoteFormData>({
    clientName: '',
    selectedClient: null,
    quoteDate: todayIsoDate(),
    status: 'pending',
    documentType: 'orcamento',
    validityDays: '7',
    warranty: '',
    warrantyAmount: null,
    warrantyUnit: null,
    notes: '',
    termsConditions: '',
    items: [],
    shipping: 0,
    discount: 0,
    discountType: 'value',
    newClientPhone: '',
    newClientAddress: '',
    newClientDocument: '',
    saveNewClient: false,
    title: '',
    showSequenceId: false,
    generalPhotos: [],
  });

  // Ref to always hold the latest formData (avoids stale closure in handleSave)
  const formDataRef = useRef<QuoteFormData>(formData);

  // Wrapper that updates both state and ref synchronously
  const setFormData = useCallback((updater: React.SetStateAction<QuoteFormData>) => {
    setFormDataInternal((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      formDataRef.current = next; // Sync ref immediately
      return next;
    });
  }, []);

  // Helper to check if form is dirty - includes ALL fields that can change
  const checkIfFormDirty = useCallback(() => {
    if (!initialFormDataRef.current) return false;
    const currentFormData = JSON.stringify({
      clientName: formData.clientName,
      selectedClient: formData.selectedClient?.id || null,
      status: formData.status,
      documentType: formData.documentType,
      validityDays: formData.validityDays,
      warranty: formData.warranty,
      notes: formData.notes,
      termsConditions: formData.termsConditions,
      // Frete/desconto contam como alteração: sem eles, mudar só o frete
      // deixaria o formulário "limpo" e o aviso de sair-sem-salvar não sairia.
      shipping: formData.shipping ?? 0,
      discount: formData.discount ?? 0,
      discountType: formData.discountType || 'value',
      // Idem para os campos do grupo 4: sem eles, mudar SÓ a forma de
      // pagamento deixaria o formulário "limpo" e o aviso de sair-sem-salvar
      // não apareceria.
      paymentMethods: formData.paymentMethods ?? null,
      paymentTerms: formData.paymentTerms || '',
      reference: formData.reference || '',
      title: formData.title,
      showSequenceId: formData.showSequenceId,
      newClientPhone: formData.newClientPhone || '',
      newClientAddress: formData.newClientAddress || '',
      newClientDocument: formData.newClientDocument || '',
      items: formData.items.map(item => ({
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount: item.discount,
        discount_type: item.discount_type,
        image_url: item.image_url,
        image_url_2: item.image_url_2,
        image_url_3: item.image_url_3,
        unit: item.unit,
        itemType: item.itemType,
      })),
      generalPhotos: (formData.generalPhotos || []).map(p => ({
        imageUrl: p.imageUrl, 
        title: p.title, 
        description: p.description 
      })),
    });
    return currentFormData !== initialFormDataRef.current;
  }, [formData]);

  // Update isFormDirty state when formData changes
  useEffect(() => {
    if (!id) return;
    const isDirty = checkIfFormDirty();
    setIsFormDirty(isDirty);
  }, [formData, id, checkIfFormDirty]);

  useEffect(() => {
    // Wait for auth to finish loading before doing anything
    if (authLoading) {
      return;
    }

    if (!id) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    // Auth finished loading but no user - wait a bit for potential session restoration
    // This handles race conditions where the session might still be loading
    if (!user) {
      // Set a timeout - if user doesn't appear, show not found
      const timeoutId = setTimeout(() => {
        setLoading(false);
        setNotFound(true);
      }, 1000); // Give 1 second for session to restore
      
      return () => clearTimeout(timeoutId);
    }

    const attemptKey = `${user.id}:${id}:${isOnline ? 'online' : 'offline'}`;

    // Se o usuário só apareceu AGORA (a sessão restaurou depois do timeout de 1s
    // acima), o `notFound` foi marcado sem que nenhuma busca tivesse acontecido
    // para esta chave — só nesse caso vale tentar de novo.
    //
    // ⚠️ A comparação é contra a chave COMPOSTA, que é o que o ref guarda. Ela já
    // foi `!== user.id`, e como o ref nunca contém um id puro o reset rodava
    // SEMPRE: `setNotFound(false)` + `setLoading(true)` reabriam o guard abaixo, a
    // busca acontecia de novo, falhava de novo — 305 consultas em 400 ms, com a
    // tela piscando entre esqueleto e "não encontrado". Bastava um link para
    // orçamento excluído (docs/testes.md §3.8).
    if (notFound && fetchAttemptedRef.current !== attemptKey) {
      setNotFound(false);
      setLoading(true);
    }

    // Já buscamos nesta combinação de usuário + orçamento + modo: não repete.
    //
    // O `&& !loading` que existia aqui era a outra metade do laço — ele deixava
    // passar qualquer render em que `loading` tivesse voltado a `true`. E ele é
    // desnecessário: os DOIS únicos pontos que levantam `loading` para forçar
    // releitura (o reset acima e o evento de despesa cobrável) já garantem que o
    // ref não bate — o reset só roda quando a chave difere, e o evento zera o ref.
    // Mantê-lo custava uma consulta duplicada mesmo no caminho feliz.
    if (fetchAttemptedRef.current === attemptKey) {
      return;
    }

    // Mark that we're attempting fetch for this user + quote + mode
    fetchAttemptedRef.current = attemptKey;

    const fetchQuote = async () => {
      try {
        let quoteData: Quote | null = null;
        let fetchedFromServer = false;
        
        // Try to fetch from server if online
        if (isOnline) {
          try {
            // Scope by the active company (team accounts) so a member can open
            // the company's quote. Falls back to id-only if company isn't ready.
            let quoteQuery = supabase
              .from('quotes')
              .select('*')
              .eq('id', id);
            if (activeCompany) {
              quoteQuery = quoteQuery.eq('company_id', activeCompany.id);
            }
            const { data, error: quoteError } = await quoteQuery.maybeSingle();
            
            if (!quoteError && data) {
              quoteData = data as Quote;
              fetchedFromServer = true;
            }
          } catch (serverError) {
            console.warn('[QuoteDetailPage] Server fetch failed, will try cache:', serverError);
          }
        }
        
        // If not fetched from server (offline or error), try cache
        if (!quoteData) {
          const cachedQuotes = getCachedQuotes(user.id);
          if (cachedQuotes) {
            const cachedQuote = cachedQuotes.find((q: any) => q.id === id);
            if (cachedQuote) {
              quoteData = cachedQuote as Quote;
              console.log('[QuoteDetailPage] Loaded quote from cache (offline mode)');
            }
          }
          
          // Also check offline queue for pending quotes
          if (!quoteData) {
            const offlineQueue = getOfflineQueue();
            const pendingOp = offlineQueue.find((op) => op.id === id && op.type === 'create_quote');
            if (pendingOp) {
              console.log('[QuoteDetailPage] Found quote in offline queue');
              // Create a temporary quote object from pending operation
              quoteData = {
                id: pendingOp.id,
                user_id: user.id,
                ...pendingOp.data.quote,
                created_at: new Date(pendingOp.createdAt).toISOString(),
                updated_at: new Date(pendingOp.createdAt).toISOString(),
                _offline_pending: true,
              } as Quote;
            }
          }
        }
        
        if (!quoteData) {
          console.error('[QuoteDetailPage] Quote not found in server, cache, or offline queue');
          setNotFound(true);
          setLoading(false);
          return;
        }

        let quote = quoteData as any;

        // If we're offline and there is a pending offline update for this quote,
        // prefer that state so reopening the quote shows the latest items/photos.
        // (This also avoids relying exclusively on cache persistence.)
        const offlineQueueSnapshot = !isOnline ? getOfflineQueue() : [];
        const pendingUpdateOps = offlineQueueSnapshot
          .filter((op) => op.type === 'update_quote' && op.data?.quoteId === id && op.data?.userId === user.id)
          .sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
        const latestPendingUpdate = pendingUpdateOps.length > 0 ? pendingUpdateOps[pendingUpdateOps.length - 1] : null;
        if (!isOnline && latestPendingUpdate?.data?.updates) {
          quote = {
            ...quote,
            ...(latestPendingUpdate.data.updates as any),
            _offline_pending: true,
          };
        }
      
      // Fetch the company associated with this quote (only if online)
      if (quote.company_id && isOnline) {
        const { data: companyData } = await supabase
          .from('companies')
          .select('*')
          .eq('id', quote.company_id)
          .maybeSingle();
        
        if (companyData) {
          setQuoteCompany(companyData as typeof activeCompany);
        }
      } else if (quote.company_id) {
        // Try from cache when offline
        const cachedCompanies = getCachedCompanies(user.id);
        if (cachedCompanies) {
          const cachedCompany = cachedCompanies.find((c: any) => c.id === quote.company_id);
          if (cachedCompany) {
            setQuoteCompany(cachedCompany);
          }
        }
      } else {
        // Fallback to active company if no company_id saved
        setQuoteCompany(activeCompany);
      }
      
       // Try to fetch the client if client_id exists
      let selectedClient: Client | null = null;
      
      // First try from cache (works for both online and offline, and for offline_ IDs)
      if (quote.client_id) {
        const cachedClients = getCachedClients(user.id);
        if (cachedClients) {
          const cachedClient = cachedClients.find((c: any) => c.id === quote.client_id);
          if (cachedClient) {
            selectedClient = cachedClient as Client;
          }
        }
      }
      
      // If not found in cache and online, try server
      if (!selectedClient && quote.client_id && isOnline && !String(quote.client_id).startsWith('offline_')) {
        const { data: clientData } = await supabase
          .from('clients')
          .select('*')
          .eq('id', quote.client_id)
          .maybeSingle();
        
        if (clientData) {
          selectedClient = clientData as Client;
          // Update cache with this client for future offline access
          const cachedClients = getCachedClients(user.id) || [];
          if (!cachedClients.find((c: any) => c.id === clientData.id)) {
            cacheClients(user.id, [...cachedClients, clientData]);
          }
        }
      }

       // Fallback: if quote has a client_name but no client_id, try to match from cached list
       if (!selectedClient && quote.client_name) {
         const cachedClients = getCachedClients(user.id);
         if (cachedClients && cachedClients.length > 0) {
           const phone = (quote as any).client_phone || null;
           const match = cachedClients.find((c: any) => {
             if (!c?.name) return false;
             if (c.name !== quote.client_name) return false;
             // If we have phone on both sides, prefer exact match
             if (phone && c.phone) return c.phone === phone;
             return true;
           });
           if (match) selectedClient = match as Client;
         }
       }

       // If still no selectedClient but we have a valid client_id (not offline_), create a virtual client object
       // This ensures online-saved clients appear correctly even when cache wasn't populated
       if (!selectedClient && quote.client_id && !String(quote.client_id).startsWith('offline_')) {
         // Create a virtual client object from quote data to display as "saved"
         selectedClient = {
           id: quote.client_id,
           name: quote.client_name || '',
           phone: (quote as any).client_phone || null,
           address: (quote as any).client_address || null,
           user_id: user.id,
           created_at: quote.created_at || new Date().toISOString(),
         } as Client;
       }
      
      // Store original client ID to detect changes later
      setOriginalClientId(quote.client_id || null);

      // Fetch quote items - try from server when online, otherwise check offline queue or cache
      let items: QuoteItem[] = [];
      let itemsLoadedFromCache = false;
      
       // IMPORTANT: if this is an offline-pending quote (id starts with offline_),
       // even when we're online we MUST load items/photos from local cache/queue.
       if (isOnline && !String(id).startsWith('offline_')) {
        const { data: itemsData, error: itemsError } = await supabase
          .from('quote_items')
          .select('*')
          .eq('quote_id', id)
          .order('position', { ascending: true });

        if (itemsError) {
          console.error('Error loading quote items:', itemsError);
        }

        items = itemsData && itemsData.length > 0
          ? (itemsData as QuoteItemType[]).map((item) => ({
              id: item.id,
              name: item.name || '',
              description: item.description || '',
              quantity: item.quantity,
              unit_price: item.unit_price,
              discount: item.discount || 0,
              discount_type: item.discount_type || 'value',
              image_url: item.image_url || undefined,
              image_url_2: (item as any).image_url_2 || undefined,
              image_url_3: (item as any).image_url_3 || undefined,
              source: normalizeItemSource(item),
              itemType: item.item_type || 'service',
              unit: item.unit || '',
              product_id: item.product_id || undefined,
              barcode: item.barcode || undefined,
              // Snapshot do custo: carregado do que foi GRAVADO, não relido do
              // catálogo — reeditar um orçamento antigo não pode reescrever o
              // custo histórico dele com o custo de hoje.
              unit_cost: item.unit_cost ?? null,
              // Vinculo com a despesa cobravel: sem ele o proximo save vira a linha em
              // item avulso e o financeiro perde o controle dela (CLAUDE.md #70).
              finance_entry_id: item.finance_entry_id ?? null,
            }))
          : [];

        // Load additional catalog photos and details for items
        const catalogProductIds = items.map(i => i.product_id).filter(Boolean);
        if (catalogProductIds.length > 0 && isOnline) {
          try {
            const { data: photosData } = await supabase
              .from('product_photos')
              .select('product_id, image_url, position')
              .in('product_id', catalogProductIds)
              .order('position');
            const photosMap: Record<string, string[]> = {};
            if (photosData) {
              photosData.forEach(p => {
                if (!photosMap[p.product_id]) photosMap[p.product_id] = [];
                photosMap[p.product_id][p.position] = p.image_url;
              });
            }

            const { data: productsData } = await supabase
              .from('products')
              .select('id, category, internal_code, track_stock, min_stock, lc116_item, iss_aliquota, iss_retido, nbs, cclasstrib')
              .in('id', catalogProductIds);
            const productsMap: Record<string, any> = {};
            if (productsData) {
              productsData.forEach(p => {
                productsMap[p.id] = p;
              });
            }

            items = items.map(item => {
              if (item.product_id) {
                const itemPhotos = photosMap[item.product_id] || [];
                const product = productsMap[item.product_id] || {};
                return {
                  ...item,
                  image_url: itemPhotos[0] || item.image_url,
                  image_url_2: itemPhotos[1] || item.image_url_2,
                  image_url_3: itemPhotos[2] || item.image_url_3,
                  category: product.category || null,
                  internal_code: product.internal_code || null,
                  track_stock: product.track_stock || false,
                  min_stock: product.min_stock || null,
                  lc116_item: product.lc116_item || null,
                  iss_aliquota: product.iss_aliquota || null,
                  iss_retido: product.iss_retido === true,
                  nbs: product.nbs || null,
                  cclasstrib: product.cclasstrib || null,
                };
              }
              return item;
            });
          } catch (err) {
            console.error('Error loading product details and photos:', err);
          }
        }
        } else {
          // Offline or quote was loaded from cache: try to get items from offline queue or cached quote
          const offlineQueue = getOfflineQueue();

          // Highest priority: pending offline update for an existing quote
          const pendingUpdates = offlineQueue
            .filter((op) => op.type === 'update_quote' && op.data?.quoteId === id && op.data?.userId === user.id)
            .sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
          const latestUpdate = pendingUpdates.length > 0 ? pendingUpdates[pendingUpdates.length - 1] : null;
          if (latestUpdate?.data?.items && Array.isArray(latestUpdate.data.items)) {
            items = latestUpdate.data.items.map((item: any, index: number) => ({
              id: item.id || `temp_${index}`,
              name: item.name || '',
              description: item.description || '',
              quantity: item.quantity,
              unit_price: item.unit_price,
              discount: item.discount || 0,
              discount_type: item.discount_type || 'value',
              image_url: item.image_url || undefined,
              image_url_2: item.image_url_2 || undefined,
              image_url_3: item.image_url_3 || undefined,
              source: normalizeItemSource(item),
              itemType: item.item_type || item.itemType || 'service',
              unit: item.unit || '',
              product_id: item.product_id || undefined,
              barcode: item.barcode || undefined,
              // Snapshot do custo: carregado do que foi GRAVADO, não relido do
              // catálogo — reeditar um orçamento antigo não pode reescrever o
              // custo histórico dele com o custo de hoje.
              unit_cost: item.unit_cost ?? null,
              // Vinculo com a despesa cobravel: sem ele o proximo save vira a linha em
              // item avulso e o financeiro perde o controle dela (CLAUDE.md #70).
              finance_entry_id: item.finance_entry_id ?? null,
            }));
            itemsLoadedFromCache = true;
          } else {
            const pendingQuote = offlineQueue.find((op) => op.id === id && op.type === 'create_quote');
        
          if (pendingQuote && pendingQuote.data.items) {
          items = pendingQuote.data.items.map((item: any, index: number) => ({
            id: item.id || `temp_${index}`,
            name: item.name || '',
            description: item.description || '',
            quantity: item.quantity,
            unit_price: item.unit_price,
            discount: item.discount || 0,
            discount_type: item.discount_type || 'value',
            image_url: item.image_url || undefined,
            image_url_2: item.image_url_2 || undefined,
            image_url_3: item.image_url_3 || undefined,
            source: normalizeItemSource(item),
            itemType: item.item_type || item.itemType || 'service',
            unit: item.unit || '',
            product_id: item.product_id || undefined,
            barcode: item.barcode || undefined,
            unit_cost: item.unit_cost ?? null,
            // Vinculo com a despesa cobravel: sem ele o proximo save vira a linha em
            // item avulso e o financeiro perde o controle dela (CLAUDE.md #70).
            finance_entry_id: item.finance_entry_id ?? null,
          }));
          itemsLoadedFromCache = true;
        } else {
          // Try to get from cached quote's embedded items (if saved with items in cache)
          const cachedQuotes = getCachedQuotes(user.id);
          if (cachedQuotes) {
            const cachedQuote = cachedQuotes.find((q: any) => q.id === id);
            if (cachedQuote && cachedQuote._offline_items && cachedQuote._offline_items.length > 0) {
              items = cachedQuote._offline_items.map((item: any, index: number) => ({
                id: item.id || `temp_${index}`,
                name: item.name || '',
                description: item.description || '',
                quantity: item.quantity,
                unit_price: item.unit_price,
                discount: item.discount || 0,
                discount_type: item.discount_type || 'value',
                image_url: item.image_url || undefined,
                image_url_2: item.image_url_2 || undefined,
                image_url_3: item.image_url_3 || undefined,
                source: normalizeItemSource(item),
                itemType: item.item_type || item.itemType || 'service',
                unit: item.unit || '',
                product_id: item.product_id || undefined,
                barcode: item.barcode || undefined,
                unit_cost: item.unit_cost ?? null,
                // Vinculo com a despesa cobravel: sem ele o proximo save vira a linha em
                // item avulso e o financeiro perde o controle dela (CLAUDE.md #70).
                finance_entry_id: item.finance_entry_id ?? null,
              }));
              itemsLoadedFromCache = true;
              console.log('[QuoteDetailPage] Loaded items from cache:', items.length);
            }
          }
        }
          }
      }

      // If quote_items has no image_url, but product was updated in Catalog with an image,
      // use Catalog image for display (online + offline).
      {
        const needs = Array.from(
          new Set(
            items
              .filter((i) => i.product_id && !i.image_url)
              .map((i) => i.product_id as string)
          )
        );

        if (needs.length > 0) {
          const map = new Map<string, string>();

          if (isOnline) {
            let productsQuery = supabase
              .from('products')
              .select('id,image_url')
              .in('id', needs);
            if (activeCompany) {
              productsQuery = productsQuery.eq('company_id', activeCompany.id);
            }
            const { data: productsData } = await productsQuery;

            (productsData || []).forEach((p: any) => {
              if (p?.id && p?.image_url) map.set(p.id, p.image_url);
            });
          } else {
            const cachedCatalog = getCachedCatalog(user.id) || [];
            cachedCatalog.forEach((p: any) => {
              if (p?.id && needs.includes(p.id) && p?.image_url) map.set(p.id, p.image_url);
            });
          }

          if (map.size > 0) {
            items = items.map((i) => {
              if (i.product_id && !i.image_url) {
                const url = map.get(i.product_id);
                if (url) return { ...i, image_url: url };
              }
              return i;
            });
          }
        }
      }

      // Fetch general photos - try from server when online, otherwise use cache
      let generalPhotos: GeneralPhoto[] = [];
      
       if (isOnline && !String(id).startsWith('offline_')) {
        const { data: photosData } = await supabase
          .from('quote_photos')
          .select('*')
          .eq('quote_id', id)
          .order('position', { ascending: true });

        generalPhotos = photosData
          ? (photosData as QuotePhoto[]).map((p) => ({
              id: p.id,
              imageUrl: p.image_url,
              title: p.title || undefined,
              description: p.description || undefined,
            }))
          : [];
      } else {
        // Offline or quote was loaded from cache: try to get photos from offline queue or cached quote
        const offlineQueue = getOfflineQueue();

        // Highest priority: pending offline update for an existing quote
        const pendingUpdates = offlineQueue
          .filter((op) => op.type === 'update_quote' && op.data?.quoteId === id && op.data?.userId === user.id)
          .sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
        const latestUpdate = pendingUpdates.length > 0 ? pendingUpdates[pendingUpdates.length - 1] : null;
        if (latestUpdate?.data?.generalPhotos && Array.isArray(latestUpdate.data.generalPhotos)) {
          generalPhotos = latestUpdate.data.generalPhotos.map((p: any) => ({
            id: p.id,
            imageUrl: p.imageUrl || p.image_url,
            title: p.title || undefined,
            description: p.description || undefined,
          }));
          console.log('[QuoteDetailPage] Loaded photos from pending update:', generalPhotos.length);
        } else {
          const pendingQuote = offlineQueue.find((op) => op.id === id && op.type === 'create_quote');
        
        if (pendingQuote && pendingQuote.data.generalPhotos && pendingQuote.data.generalPhotos.length > 0) {
          generalPhotos = pendingQuote.data.generalPhotos.map((p: any) => ({
            id: p.id,
            imageUrl: p.imageUrl || p.image_url,
            title: p.title || undefined,
            description: p.description || undefined,
          }));
          console.log('[QuoteDetailPage] Loaded photos from offline queue:', generalPhotos.length);
        } else {
          // Try to get from cached quote's embedded photos
          const cachedQuotes = getCachedQuotes(user.id);
          if (cachedQuotes) {
            const cachedQuote = cachedQuotes.find((q: any) => q.id === id);
            if (cachedQuote && cachedQuote._offline_photos && cachedQuote._offline_photos.length > 0) {
              generalPhotos = cachedQuote._offline_photos.map((p: any) => ({
                id: p.id,
                imageUrl: p.imageUrl || p.image_url,
                title: p.title || undefined,
                description: p.description || undefined,
              }));
              console.log('[QuoteDetailPage] Loaded photos from cache:', generalPhotos.length);
            }
          }
        }
        }
      }

      setQuoteDate(quote.created_at ? todayIsoDate(new Date(quote.created_at)) : todayIsoDate());
      
      const clientPhone = quote.client_phone || selectedClient?.phone || '';
      const clientAddress = quote.client_address || selectedClient?.address || '';
      const clientDocument = (quote as any).client_document || selectedClient?.document || '';

      const loadedFormData: QuoteFormData = {
        clientName: quote.client_name || '',
        selectedClient,
        quoteDate: quote.created_at ? todayIsoDate(new Date(quote.created_at)) : todayIsoDate(),
        status: quote.status as any,
        documentType: quote.document_type || 'orcamento',
        validityDays: quote.validity_days?.toString() || '7',
        ...quoteWarrantyFromRow(quote),
        // Subtitulo + local de emissao. Mesma armadilha #70 dos campos acima:
        // perder a LEITURA faz o proximo save gravar vazio por cima e o
        // cabecalho do documento voltar sozinho ao que era.
        ...quoteHeadingFromRow(quote),
        extraFields: quoteExtraFieldsFromRow(quote),
        notes: quote.notes || '',
        termsConditions: quote.terms_conditions || '',
        items,
        // Frete e desconto de CABEÇALHO. `quoteHeaderFromRow` normaliza o
        // `numeric` que o PostgREST/cache pode entregar como string e o
        // `discount_type` nulo — se a leitura perder um destes, o próximo save
        // grava zero e apaga o frete do orçamento sem erro nenhum (é o mesmo
        // modo de falha do snapshot de custo, armadilha #62).
        ...quoteHeaderFromRow(
          quote as { shipping?: number | string | null; discount?: number | string | null; discount_type?: string | null },
        ),
        // Formas aceitas / condições / referência (grupo 4). Mesma armadilha
        // #70 do frete: se a LEITURA perder um destes, a tela reabre sem ele e
        // o próximo save grava `null` — a escolha do usuário some sozinha. No
        // caso das formas o efeito colateral é pior: `payment_methods` voltaria
        // a `null` e o QR PIX reapareceria num documento onde ele foi tirado.
        ...quotePaymentInfoFromRow(
          quote as { payment_methods?: unknown; payment_terms?: string | null; reference?: string | null },
        ),
        // Modelo do PDF deste documento. Mesma armadilha #70: se a leitura
        // perder o campo, o próximo save grava null e a escolha do usuário
        // volta sozinha ao padrão da empresa.
        pdfTemplate: quotePdfTemplateFromRow(quote as { pdf_template?: string | null }),
        // Populate client phone and address from quote first, fallback to selected client
        newClientPhone: clientPhone,
        newClientAddress: clientAddress,
        newClientDocument: clientDocument,
        // Title and sequence
        title: quote.title || '',
        sequenceId: quote.sequence_id || undefined,
        showSequenceId: quote.show_sequence_id || false,
        // General photos
        generalPhotos,
      };
      
      // Check if there's a saved draft for this quote
      const draftKey = getEditDraftKey(id);
      const savedDraft = localStorage.getItem(draftKey);
      
      let finalFormData = loadedFormData;
      
      if (savedDraft) {
        try {
          const draftData = JSON.parse(savedDraft);
          // Merge draft items with loaded items to preserve source/product_id from DB
          let mergedItems = loadedFormData.items;
          if (draftData.items && Array.isArray(draftData.items)) {
            mergedItems = draftData.items.map((draftItem: QuoteItem) => {
              // Find matching item from DB by id to restore source/product_id
              const dbItem = loadedFormData.items.find(i => i.id === draftItem.id);
              return {
                ...draftItem,
                // Preserve source and product_id from DB item if exists
                 source: (draftItem.source as any) || dbItem?.source || normalizeItemSource(draftItem),
                product_id: dbItem?.product_id || draftItem.product_id,
              };
            });
          }
          // Restore draft data with properly merged items
          finalFormData = {
            ...loadedFormData,
            ...draftData,
            items: mergedItems,
            // Keep the original selectedClient and sequenceId from DB
            selectedClient: loadedFormData.selectedClient,
            sequenceId: loadedFormData.sequenceId,
          };
          setFormData(finalFormData);
        } catch (error) {
          console.error('Error restoring edit draft:', error);
          setFormData(loadedFormData);
        }
      } else {
        setFormData(loadedFormData);
      }

      // Itens gravados (não os do rascunho local): base do acerto de estoque.
      setPersistedItems(loadedFormData.items ?? []);
      setPersistedUpdatedAt((quote as any).updated_at ?? null);

      // Store initial state for dirty checking AFTER potentially restoring draft
      // This ensures we compare against the actual loaded state (with or without draft)
      // MUST include ALL fields that checkIfFormDirty compares
      const initialStateJson = JSON.stringify({
        clientName: finalFormData.clientName,
        selectedClient: finalFormData.selectedClient?.id || null,
        status: finalFormData.status,
        documentType: finalFormData.documentType,
        validityDays: finalFormData.validityDays,
        warranty: finalFormData.warranty,
        notes: finalFormData.notes,
        termsConditions: finalFormData.termsConditions,
        shipping: finalFormData.shipping ?? 0,
        discount: finalFormData.discount ?? 0,
        discountType: finalFormData.discountType || 'value',
        paymentMethods: finalFormData.paymentMethods ?? null,
        paymentTerms: finalFormData.paymentTerms || '',
        reference: finalFormData.reference || '',
        title: finalFormData.title,
        showSequenceId: finalFormData.showSequenceId,
        newClientPhone: finalFormData.newClientPhone || '',
        newClientAddress: finalFormData.newClientAddress || '',
        newClientDocument: finalFormData.newClientDocument || '',
        items: finalFormData.items.map(item => ({
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount,
          discount_type: item.discount_type,
          image_url: item.image_url,
          image_url_2: item.image_url_2,
          image_url_3: item.image_url_3,
          unit: item.unit,
          itemType: item.itemType,
        })),
        generalPhotos: (finalFormData.generalPhotos || []).map(p => ({ 
          imageUrl: p.imageUrl, 
          title: p.title, 
          description: p.description 
        })),
      });
      initialFormDataRef.current = initialStateJson;
      
      // Only set dirty if draft was restored AND has actual changes from DB
      // Compare draft data against original DB data to determine if truly dirty
      if (savedDraft) {
        const dbStateJson = JSON.stringify({
          clientName: loadedFormData.clientName,
          selectedClient: loadedFormData.selectedClient?.id || null,
          status: loadedFormData.status,
          documentType: loadedFormData.documentType,
          validityDays: loadedFormData.validityDays,
          warranty: loadedFormData.warranty,
          notes: loadedFormData.notes,
          termsConditions: loadedFormData.termsConditions,
          title: loadedFormData.title,
          showSequenceId: loadedFormData.showSequenceId,
          newClientPhone: loadedFormData.newClientPhone || '',
          newClientAddress: loadedFormData.newClientAddress || '',
          newClientDocument: loadedFormData.newClientDocument || '',
          items: items.map(item => ({
            name: item.name,
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price,
            discount: item.discount,
            discount_type: item.discount_type,
            image_url: item.image_url,
            image_url_2: item.image_url_2,
            image_url_3: item.image_url_3,
            unit: item.unit,
            itemType: item.itemType,
          })),
          generalPhotos: generalPhotos.map(p => ({ imageUrl: p.imageUrl, title: p.title, description: p.description })),
        });
        // Only mark dirty if restored draft differs from DB
        setIsFormDirty(initialStateJson !== dbStateJson);
      } else {
        // No draft was restored - start with clean state (not dirty)
        setIsFormDirty(false);
      }
      
      // Cache the quote with items and photos for offline access
      // (also important when quote header came from cache but nested data was fetched after reconnect)
      const shouldCacheNested = Boolean(user && !String(quote.id).startsWith('offline_'));
      if (shouldCacheNested && user) {
        const cachedQuotes = getCachedQuotes(user.id) || [];
        const existingIndex = cachedQuotes.findIndex((q: any) => q.id === id);
        
        // Convert items to a format suitable for caching
        const itemsForCache = items.map((item: QuoteItem) => ({
          id: item.id,
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount,
          discount_type: item.discount_type,
          image_url: item.image_url,
          image_url_2: item.image_url_2,
          image_url_3: item.image_url_3,
          item_type: item.itemType,
          unit: item.unit,
          product_id: item.product_id,
          barcode: item.barcode,
          // O cache alimenta a edição offline: sem o snapshot aqui, salvar sem
          // rede zeraria o custo já gravado do item.
          unit_cost: item.unit_cost ?? null,
          // Vinculo com a despesa cobravel: sem ele o proximo save vira a linha em
          // item avulso e o financeiro perde o controle dela (CLAUDE.md #70).
          finance_entry_id: item.finance_entry_id ?? null,
        }));
        
        // Convert photos to a format suitable for caching
        const photosForCache = generalPhotos.map((p: GeneralPhoto) => ({
          id: p.id,
          image_url: p.imageUrl,
          imageUrl: p.imageUrl,
          title: p.title,
          description: p.description,
        }));
        
        const quoteWithNestedData = {
          ...quote,
          _offline_items: itemsForCache,
          _offline_photos: photosForCache,
        };
        
        if (existingIndex !== -1) {
          cachedQuotes[existingIndex] = quoteWithNestedData;
        } else {
          cachedQuotes.unshift(quoteWithNestedData);
        }
        
        cacheQuotes(user.id, cachedQuotes);
        console.log('[QuoteDetailPage] Cached quote with items and photos for offline access');
      }

      setLoading(false);
      } catch (error) {
        console.error('[QuoteDetailPage] Error fetching quote:', error);
        setNotFound(true);
        setLoading(false);
      }
    };

    fetchQuote();
  }, [id, user, authLoading, notFound, loading, isOnline]);

  // Uma despesa cobrável cria/remove uma LINHA deste orçamento e regrava
  // `quotes.total` direto no banco — por fora deste formulário. Sem recarregar,
  // a tela seguiria mostrando o total antigo e sem o item, e o próximo save
  // (que apaga e reinsere os itens a partir do estado local) APAGARIA a linha
  // recém-criada. Limpar o `fetchAttemptedRef` é o que destrava o efeito acima.
  useEffect(() => {
    const handler = () => {
      fetchAttemptedRef.current = null;
      setLoading(true);
    };
    window.addEventListener(BILLABLE_EXPENSES_UPDATED_EVENT, handler);
    return () => window.removeEventListener(BILLABLE_EXPENSES_UPDATED_EVENT, handler);
  }, []);

  // Auto-save draft with debounce - ONLY if form is dirty
  useEffect(() => {
    if (!id || !isFormDirty) return;
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new debounced timer (1 second delay)
    debounceTimerRef.current = setTimeout(() => {
      try {
        const draftKey = getEditDraftKey(id);
        localStorage.setItem(draftKey, JSON.stringify({
          ...formData,
          quoteId: id,
        }));
        // Dispatch event to notify DraftIndicator
        window.dispatchEvent(new StorageEvent('storage', { key: draftKey }));
      } catch (error) {
        console.error('Error saving edit draft:', error);
      }
    }, 1000);
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [formData, id, isFormDirty]);

  // Clear edit draft on unmount if not dirty
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Function to clear the edit draft
  const clearEditDraft = useCallback(() => {
    if (!id) return;
    // O documento acabou de virar dono das fotos que ainda referencia; as que
    // sobraram (trocadas, removidas, ou de um item excluído antes do save)
    // ficam sem ninguém apontando para elas. Zerar a lista na sequência é
    // obrigatório: esta tela continua montada depois de salvar, e um descarte
    // de rascunho posterior apagaria as fotos que a gravação oficializou.
    // Pelo REF, não pelo estado: este `useCallback` tem `[id]` nas deps, e um
    // `formData` capturado aqui seria o do render em que ele foi criado — a
    // lista de pendentes chegaria vazia justamente depois de um upload.
    void releaseSavedUploads(formDataRef.current);
    setFormData((prev) => (prev.pendingUploads?.length ? { ...prev, pendingUploads: [] } : prev));
    try {
      const draftKey = getEditDraftKey(id);
      localStorage.removeItem(draftKey);
      setIsFormDirty(false);
      // Dispatch event to hide DraftIndicator
      window.dispatchEvent(new StorageEvent('storage', { key: draftKey }));
    } catch (error) {
      console.error('Error clearing edit draft:', error);
    }
  }, [id]);

  const total = calculateTotal(formData);

  /**
   * Contexto do orçamento para os Modelos de Mensagens. Valores CRUS — a
   * formatação pt-BR (moeda, dd/mm/aaaa) acontece dentro do renderer, e a
   * codificação da URL só depois disso (ver src/lib/messageTemplates.ts).
   * O mesmo builder é usado no card do dashboard, para `{validade}` não divergir.
   */
  const messageContext = buildQuoteMessageContext({
    clientName: formData.selectedClient?.name || formData.clientName,
    sequenceId: formData.sequenceId,
    total,
    issuedAt: formData.quoteDate,
    validityDays: formData.validityDays,
    quoteId: id,
    buildLink: getPublicQuoteUrl,
    companyName: quoteCompany?.name || activeCompany?.name,
    title: formData.title,
  });

  const clientPhoneForMessage =
    formData.selectedClient?.phone || formData.newClientPhone || '';

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  // Helper function to convert image URL to Base64 (for CORS/async loading)
  const getDataUrl = async (url: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          } else {
            resolve(null);
          }
        } catch (e) {
          console.error('Error converting image to base64:', e);
          resolve(null);
        }
      };
      img.onerror = () => {
        console.error('Error loading image:', url);
        resolve(null);
      };
      img.src = url;
    });
  };

  /**
   * Busca a assinatura de aprovação para carimbar no PDF. Só o dono do
   * orçamento enxerga `quote_approvals` (RLS) — qualquer falha (offline, id
   * temporário, orçamento ainda pendente) devolve null e o PDF degrada para a
   * linha em branco, sem quebrar o download.
   */
  const fetchApprovalSignature = async () => {
    const approved = formData.status === 'approved' || formData.status === 'completed';
    if (!approved || !isOnline || !id || String(id).startsWith('offline_')) return null;
    try {
      const { data } = await supabase
        .from('quote_approvals')
        .select('signature, approver_name, approved_at')
        .eq('quote_id', id)
        .maybeSingle();
      return data;
    } catch {
      return null;
    }
  };

  const generatePDF = async (paper: PaperFormat = 'a4') => {
    // Build PDF data using the unified generator with quoteCompany data (the company saved with the quote)
    const clientPhone = formData.selectedClient?.phone || formData.newClientPhone;
    const clientAddress = formData.selectedClient?.address || formData.newClientAddress;
    const clientDocument = onlyDigits(formData.selectedClient?.document || formData.newClientDocument || '') || null;
    
    // Use quoteCompany (from DB) instead of activeCompany
    const companyForPDF = quoteCompany || activeCompany;
    
    // Helper function to convert hex to RGB
    const hexToRgb = (hex: string): [number, number, number] => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (result) {
        return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
      }
      return [16, 185, 129]; // Default emerald
    };
    
    // Assinatura que o cliente desenhou ao aprovar pelo link público. É lida do
    // servidor (RLS: só o dono do orçamento), então offline/erro simplesmente
    // devolve nada e o PDF sai com a linha em branco de sempre.
    const approval = await fetchApprovalSignature();

    const pdfData: PDFQuoteData = {
      documentType: (formData.documentType || 'orcamento') as PDFQuoteData['documentType'],
      // O título do prestador SUBSTITUI a palavra do tipo no cabeçalho do PDF
      // (`resolveDocumentTitle`). Vazio cai no rótulo de sempre, então o
      // documento de quem não titula sai byte a byte igual ao histórico.
      title: formData.title,
      // Subtitulo + local de emissao: sem eles o PDF nao teria por onde
      // saber o cabecalho que o usuario montou (armadilha #70, agora do
      // lado do PAYLOAD e nao da hidratacao).
      subtitle: formData.subtitle,
      issuePlace: formData.issuePlace,
      extraFields: formData.extraFields,
      client: {
        name: formData.clientName || 'Não informado',
        phone: clientPhone || undefined,
        address: clientAddress || undefined,
        document: clientDocument || undefined,
      },
      company: companyForPDF ? {
        name: companyForPDF.name || 'Minha Empresa',
        cnpj: companyForPDF.cnpj_cpf || undefined,
        address: companyForPDF.address || undefined,
        phone: companyForPDF.phone || undefined,
        email: companyForPDF.email || undefined,
        logoUrl: companyForPDF.logo_url || undefined,
        pixKey: companyForPDF.pix_key || undefined,
        pixCity: companyForPDF.pix_city || undefined,
      } : undefined,
      quoteDate: formData.quoteDate,
      validityDays: formData.validityDays ? parseInt(formData.validityDays) : undefined,
      warranty: warrantyDisplayText(formData) || undefined,
      notes: formData.notes || undefined,
      termsConditions: formData.termsConditions || undefined,
      items: formData.items.map(item => ({
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount: item.discount || 0,
        discount_type: item.discount_type || 'value',
        unit: item.unit,
        image_url: item.image_url,
      })),
      total,
      // Frete e desconto de CABEÇALHO: sem eles o PDF imprimiria só a linha de
      // total, sem a decomposição, e o cliente não veria de onde veio o número.
      shipping: formData.shipping ?? 0,
      discount: formData.discount ?? 0,
      discountType: formData.discountType || 'value',
      // Campos de EXIBIÇÃO do grupo 4. `paymentMethods` chega ao PDF
      // PRESERVANDO o `null`: é ele que mantém o QR PIX nos documentos
      // anteriores a esta feature (ver `src/lib/quotePaymentInfo.ts`).
      paymentMethods: formData.paymentMethods ?? null,
      paymentTerms: formData.paymentTerms || null,
      reference: formData.reference || null,
      // QR estático × dinâmico. O modo vem do interruptor da empresa; a cobrança
      // (quando existe) VENCE o modo, porque a página pública `/o/:id` só
      // enxerga a cobrança e as duas telas não podem discordar sobre como pagar.
      // Ver `src/lib/asaasCharge.ts`.
      pixQrMode: paymentDetection.resolution.mode,
      pixDynamic: quoteCharge.pixDynamic,
      isPremium: noBrandingFooter.allowed,
      brandColorRGB: companyForPDF ? hexToRgb(companyForPDF.primary_color) : [16, 185, 129],
      // O escolhido NESTE documento, senão o padrão da empresa (o dispatcher
      // ainda força 'classico' para conta grátis — regra de plano fica lá).
      template: resolveQuotePdfTemplate(
        formData.pdfTemplate,
        (companyForPDF as { pdf_template?: string | null } | null)?.pdf_template,
      ),
      sequenceId: formData.sequenceId,
      showSequenceId: formData.showSequenceId,
      generalPhotos: formData.generalPhotos?.map(p => ({
        imageUrl: p.imageUrl,
        title: p.title || undefined,
        description: p.description || undefined,
      })),
      // Assinatura salva do prestador (PRO) + a do cliente na aprovação.
      providerSignatureDataUrl: companyForPDF?.signature_url || undefined,
      // Só tem efeito quando o documento é `pedido` — a opção é do PDV, mas o
      // formulário também permite escolher esse tipo, e os dois têm que
      // concordar sobre a linha de assinatura do mesmo documento.
      pdvSignatureLine: companyForPDF?.pdv_signature_line ?? true,
      // Marca-d'agua da logo (config da empresa). `=== true` e nao `Boolean`:
      // ausente/indefinida cai no padrao DESLIGADO, que e o historico.
      logoWatermark: companyForPDF?.logo_watermark === true,
      clientSignatureDataUrl: approval?.signature || undefined,
      clientSignerName: approval?.approver_name || undefined,
      clientSignedAt: approval?.approved_at || undefined,
    };

    // O sufixo separa a via de bobina da via A4 no gerenciador de arquivos: as
    // duas saem do mesmo orçamento, no mesmo diretório.
    const paperSuffix = isThermalFormat(paper) ? `-${thermalSpec(paper).paperWidth}mm` : '';
    const filename = `orcamento-${formData.clientName || 'sem-nome'}${paperSuffix}-${Date.now()}.pdf`;
    await downloadQuotePDF(pdfData, filename, { paper });
  };

  /**
   * Salvar-e-gerar. O cupom térmico entra por AQUI, e não por um caminho
   * próprio, justamente porque esta tela é de edição: imprimir direto do estado
   * na tela entregaria ao cliente um documento diferente do que ficou gravado.
   * O `paper` só troca o formato da folha no fim do mesmo fluxo.
   *
   * Devolve `false` quando NÃO gerou documento (validação barrou, erro offline).
   * O botão de impressão térmica usa esse retorno para não anunciar "cupom
   * gerado" logo abaixo de um "Informe o nome do cliente" — duas mensagens
   * contraditórias na tela, sem nada impresso.
   */
  const handleDownloadPDF = async (paper: PaperFormat = 'a4'): Promise<boolean> => {
    const thermal = isThermalFormat(paper);
    // Snapshot usando REF (sempre contém o estado mais recente)
    const currentData = formDataRef.current;

    // Validate before saving
    if (!currentData.clientName.trim()) {
      toast.warning('Informe o nome do cliente');
      return false;
    }
    if (currentData.items.some((item) => !item.name.trim())) {
      toast.warning('Preencha o nome de todos os itens');
      return false;
    }

    // Offline: generate the PDF straight from the on-screen state without a
    // server round-trip. Images are embedded from the offline byte cache
    // (IndexedDB) via resolveMediaUrl, so photos/logo still appear. To persist
    // any edits, the user uses "Salvar" (which works offline) separately.
    if (!isOnline) {
      try {
        setSaving(true);
        await generatePDF(paper);
        // No térmico quem avisa é o próprio botão (ele sabe a bobina e explica
        // o passo seguinte); dois toasts para a mesma ação só empilhariam.
        if (!thermal) {
          toast.success('PDF gerado offline! Imagens já vistas online aparecem normalmente.', {
            icon: '📴',
          });
        }
        return true;
      } catch (e) {
        console.error('[QuoteDetailPage] Offline PDF error:', e);
        toast.error('Erro ao gerar o PDF offline.');
        return false;
      } finally {
        setSaving(false);
      }
    }

    /*
     * GRAVA PELO MESMO CAMINHO DO "SALVAR", em vez de ter payload próprio.
     *
     * Aqui existia uma SEGUNDA lista explícita de colunas, e ela já tinha
     * envelhecido: não gravava `title` nem `show_sequence_id`, então editar o
     * título e apertar "Baixar PDF" imprimia o título novo no papel, gravava o
     * antigo no banco e ainda anunciava "salvo com sucesso" — o título "voltava
     * sozinho" ao recarregar (a armadilha #33/#62, agora com o agravante do
     * #202: o título É o cabeçalho do documento). Ela também apagava e
     * reinseria os itens SEM olhar o erro (o fantasma do #109: documento
     * aprovado, total cheio, ZERO itens) e não persistia as fotos gerais.
     *
     * Delegar mata os três de uma vez e, mais importante, torna impossível que
     * as duas listas voltem a divergir: passou a existir uma só.
     */
    const podeGravar = Boolean(user && id && canWrite);
    if (podeGravar) {
      // `silent`: a mensagem de sucesso desta ação é a de baixo ("salvo e PDF
      // gerado"); duas seguidas para o mesmo gesto só empilhariam.
      const gravou = await handleSave({ silent: true });
      // `handleSave` já disse o motivo — anunciar "PDF gerado" por cima de um
      // "Erro ao salvar" seriam duas mensagens contraditórias na tela (#117).
      if (!gravou) return false;
    }

    try {
      setSaving(true);
      await generatePDF(paper);
    } finally {
      setSaving(false);
    }

    // Somente leitura (e visitante) não gravam nada: o PDF sai do que está na
    // tela, e o texto não pode prometer uma gravação que não aconteceu.
    if (!thermal) {
      toast.success(
        podeGravar ? 'Orçamento salvo e PDF gerado com sucesso!' : 'PDF gerado com sucesso!',
      );
    }
    return true;
  };

  /**
   * Grava o orçamento (online ou na fila offline).
   *
   * DEVOLVE `false` quando NÃO gravou — validação barrou, papel barrou, ou o
   * banco recusou —, e nesse caso o próprio `handleSave` já avisou o motivo. É o
   * canal explícito do #117: quem chama (o "Baixar PDF" e o `saveBeforeDerive`)
   * não pode assumir que "terminou" significa "gravou", senão anuncia sucesso
   * por cima de um documento que não mudou.
   *
   * `silent` cala só o toast de SUCESSO, para o chamador que tem uma mensagem
   * própria ("Orçamento salvo e PDF gerado"). Os avisos de falha nunca são
   * silenciados.
   */
  const handleSave = async (opts?: { silent?: boolean }): Promise<boolean> => {
    if (!canWrite) {
      toast.warning('Você tem acesso somente leitura nesta empresa.', { id: 'readonly-access' });
      return false;
    }
    if (!formData.clientName.trim()) {
      toast.warning('Informe o nome do cliente');
      return false;
    }
    if (formData.items.some((item) => !item.name.trim())) {
      toast.warning('Preencha o nome de todos os itens');
      return false;
    }

    // Offline: allow editing saved (online-created) quotes and sync later
    if (!isOnline) {
      if (!user || !id) {
        toast.warning('Você precisa estar logado para salvar este orçamento offline.');
        return false;
      }

      setSaving(true);
      try {
        const data: QuoteFormData = JSON.parse(JSON.stringify(formDataRef.current));

        const clientName = data.clientName.trim();
        const clientPhone = data.newClientPhone?.trim() || null;
        // Linha ÚNICA do endereço: o que o usuário digitou na linha livre vence,
        // senão ela é COMPOSTA dos campos estruturados. Ler `newClientAddress`
        // cru (o que estava aqui) fazia quem preenchesse só CEP+número gravar o
        // documento sem endereço nenhum — ver `src/lib/clientAddress.ts`.
        const clientAddress = resolveClientAddressLine(data);
        const clientDocument = onlyDigits(data.selectedClient?.document || data.newClientDocument || '') || null;
        const items: QuoteItem[] = Array.isArray(data.items) ? data.items : [];

        const updates = {
          client_id: data.selectedClient?.id || null,
          client_name: clientName,
          client_phone: clientPhone,
          client_address: clientAddress,
          client_document: clientDocument,
          company_id: quoteCompany?.id || activeCompany?.id || null,
          status: data.status,
          document_type: data.documentType || 'orcamento',
          validity_days: parseInt(data.validityDays) || null,
          ...buildQuoteWarrantyColumns(data),
          ...buildQuoteHeadingColumns(data),
          ...buildQuoteExtraFieldsColumns(data),
          notes: data.notes || null,
          terms_conditions: data.termsConditions || null,
          ...buildQuoteTotalsColumns({ ...data, items }),
          ...buildQuotePaymentColumns(data),
          ...buildQuotePdfTemplateColumns(data),
          title: data.title?.trim() || null,
          show_sequence_id: data.showSequenceId || false,
          updated_at: new Date().toISOString(),
        };

        const generalPhotos = data.generalPhotos || [];

        // Save client offline if marked
        let offlineClientId: string | null = data.selectedClient?.id || null;
        if (data.saveNewClient && !data.selectedClient && clientName) {
          // Builder ÚNICO das colunas de `clients` (src/lib/clientAddress.ts), o
          // mesmo do `NewQuotePage` e do `persistQuoteOffline`. Escrita à mão, a
          // lista daqui tinha só name/phone/address/document: e-mail, CEP e todo
          // o endereço estruturado que o usuário acabou de digitar sumiam com
          // toast de sucesso e sem erro nenhum (armadilha #33/#62).
          const clientData = buildNewClientColumns(data);

          // `companyId` explícito: a RLS de team accounts exige `company_id`
          // gravável no insert (#4), e é o executor da fila quem grava.
          const clientOperationId = addToOfflineQueue('create_client', {
            client: clientData,
            userId: user.id,
            companyId: activeCompany?.id ?? null,
          });
          
          // Add to client cache
          const cachedClients = getCachedClients(user.id) || [];
          cachedClients.unshift({
            id: clientOperationId,
            ...clientData,
            user_id: user.id,
            created_at: new Date().toISOString(),
            _offline_pending: true,
          });
          cacheClients(user.id, cachedClients);
          window.dispatchEvent(new CustomEvent('clients-updated'));
          
          offlineClientId = clientOperationId;
        }
        
        // Save catalog items offline if marked
        for (const item of items) {
          if (item.saveToCategory && item.name?.trim() && !item.product_id) {
            const itemData = {
              name: item.name,
              price: item.unit_price,
              // Custo do item, quando o usuário informou junto do
              // "salvar no catálogo". `quoteItemUnitCost` preserva 0 e devolve null
              // para ausência — `Number(null) === 0` gravaria "de graça"
              // (armadilha #61). Payload por LISTA EXPLÍCITA: omitir esta linha grava
              // o produto com custo em branco, com toast de sucesso e sem erro
              // nenhum (armadilha #33/#62).
              cost: quoteItemUnitCost(item),
              // O resto das colunas de cadastro vem do builder único: escrito à
              // mão, este payload ficava sem categoria, código interno, estoque
              // e classificação fiscal, e o produto subia pela metade quando a
              // rede voltasse. Variações e materiais NÃO entram — são
              // online-only (#30c/#65) e a UI já recusa sem conexão.
              ...catalogProductColumnsFromQuoteItem(item),
              image_url: item.image_url || null,
            };
            
            // Add to offline queue
            const catalogOperationId = addToOfflineQueue('create_catalog_item', { item: itemData, userId: user.id });
            
            // Add to catalog cache
            const cachedCatalog = getCachedCatalog(user.id) || [];
            cachedCatalog.unshift({
              id: catalogOperationId,
              ...itemData,
              user_id: user.id,
              created_at: new Date().toISOString(),
              _offline_pending: true,
            });
            // Mesmo retry de cota do `saveCatalogItemOffline` (defeito D9):
            // `item.image_url` aqui é base64 quando a foto foi tirada offline, e
            // sem o retry a gravação é recusada e o item recém-salvo some da
            // lista e do CatalogSelector — sem erro nenhum. As imagens cheias
            // seguem na op da fila.
            if (!cacheCatalog(user.id, cachedCatalog)) {
              cacheCatalog(user.id, cachedCatalog.map(stripCachedCatalogMedia));
            }

            // Update item's product_id for cache
            item.product_id = catalogOperationId;
            item.source = 'catalog';
          }
        }
        window.dispatchEvent(new CustomEvent('catalog-updated'));

        // Build DB-friendly items payload for later sync (AFTER we may have assigned product_id above)
        const itemsForDb = items.map((item, index) => buildQuoteItemRow(item, index));

        // Update client_id in updates if we created one offline
        const finalUpdates = {
          ...updates,
          client_id: offlineClientId,
        };

        // If this quote itself was created offline and hasn't synced yet,
        // fold the edit into the pending create op instead of enqueuing an
        // update that would ship the offline_ id to Postgres.
        const folded = String(id).startsWith('offline_')
          ? handleOfflineMutationOfPendingRecord(id, {
              updates: finalUpdates,
              items: itemsForDb,
              generalPhotos,
            })
          : { removed: false };

        if (!folded.removed) {
          addToOfflineQueue('update_quote', {
            quoteId: id,
            userId: user.id,
            updates: finalUpdates,
            items: itemsForDb,
            generalPhotos,
          });
        }

        // Update cache so reopen offline shows the edited state
        const cachedQuotes = (getCachedQuotes(user.id) || []) as any[];
        const idx = cachedQuotes.findIndex((q) => q.id === id);

        const itemsForCache = items.map((item: QuoteItem, i: number) => ({
          id: item.id || `temp_${i}`,
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount,
          discount_type: item.discount_type,
          image_url: item.image_url,
          image_url_2: item.image_url_2,
          image_url_3: item.image_url_3,
          item_type: item.itemType,
          unit: item.unit,
          product_id: item.product_id,
          source: item.source,
          barcode: item.barcode,
          unit_cost: item.unit_cost ?? null,
          // Vinculo com a despesa cobravel: sem ele o proximo save vira a linha em
          // item avulso e o financeiro perde o controle dela (CLAUDE.md #70).
          finance_entry_id: item.finance_entry_id ?? null,
        }));

        const photosForCache = (generalPhotos || []).map((p: GeneralPhoto) => ({
          id: p.id,
          image_url: p.imageUrl,
          imageUrl: p.imageUrl,
          title: p.title,
          description: p.description,
        }));

        const base = idx !== -1 ? cachedQuotes[idx] : { id, user_id: user.id };
        const updated = {
          ...base,
          ...finalUpdates,
          _offline_pending: true,
          _offline_items: itemsForCache,
          _offline_photos: photosForCache,
        };

        if (idx !== -1) cachedQuotes[idx] = updated;
        else cachedQuotes.unshift(updated);
        cacheQuotes(user.id, cachedQuotes);

        window.dispatchEvent(new CustomEvent('offline-queue-updated'));
        window.dispatchEvent(new CustomEvent('quotes-updated'));
        clearEditDraft();

        if (!opts?.silent) {
          toast.success('Alterações salvas localmente! Será sincronizado quando houver internet.', {
            icon: '📴',
          });
        }
        return true;
      } catch (e) {
        console.error('[QuoteDetailPage] Offline save error:', e);
        toast.error('Erro ao salvar localmente.');
        return false;
      } finally {
        setSaving(false);
      }
    }

    // NOTE: Editing an existing quote does NOT consume credits
    // Credits are only consumed when: creating new, duplicating, or changing client (which creates a new quote)

    setSaving(true);

    // Snapshot inicial usando REF (sempre contém o estado mais recente, evita stale closure)
    const data: QuoteFormData = JSON.parse(JSON.stringify(formDataRef.current));

    const clientName = data.clientName.trim();
    const clientPhone = data.newClientPhone?.trim() || null;
    // Ver a nota do ramo offline: linha livre vence, senão compõe dos campos
    // estruturados (`src/lib/clientAddress.ts`).
    const clientAddress = resolveClientAddressLine(data);
    const clientDocument = onlyDigits(data.selectedClient?.document || data.newClientDocument || '') || null;
    const items: QuoteItem[] = Array.isArray(data.items) ? data.items : [];

    const selectedClientId = data.selectedClient?.id || null;
    const selectedClient = data.selectedClient;

    let clientIdToUse = selectedClientId;
    let clientSaved = false;

    try {
      // Passo 1: Cliente (await explícito)
      if (data.saveNewClient && !data.selectedClient && clientName && user) {
        const { data: newClient, error: clientError } = await supabase
          .from('clients')
          .insert({
            user_id: user.id,
            company_id: activeCompany?.id ?? null,
            // Mesmo builder do `NewQuotePage` e da fila offline: a lista de
            // colunas de `clients` vive num lugar só (armadilha #33/#62).
            ...buildNewClientColumns(data),
          })
          .select('id')
          .maybeSingle();

        if (clientError || !newClient) {
          throw new Error(clientError?.message || 'Erro ao salvar cliente');
        }

        clientIdToUse = newClient.id;
        clientSaved = true;
      } else if (selectedClient && user) {
        // Update existing client's phone/address if changed
        const phoneChanged = (clientPhone || '') !== (selectedClient.phone || '');
        const addressChanged = (clientAddress || '') !== (selectedClient.address || '');

        if (phoneChanged || addressChanged) {
          const { error: updateClientError } = await supabase
            .from('clients')
            .update({
              phone: clientPhone,
              address: clientAddress,
            document: clientDocument,
            })
            .eq('id', selectedClient.id);

          if (updateClientError) {
            console.error('Client update error:', updateClientError);
          }
        }
      }

      // Passo 3: Orçamento (final)
      // Update company_id if it wasn't set before (for legacy quotes)
      const { error: quoteError } = await supabase
        .from('quotes')
        .update({
          client_id: clientIdToUse,
          client_name: clientName,
          client_phone: clientPhone,
          client_address: clientAddress,
          client_document: clientDocument,
          company_id: quoteCompany?.id || activeCompany?.id || null,
          status: data.status,
          document_type: data.documentType || 'orcamento',
          validity_days: parseInt(data.validityDays) || null,
          ...buildQuoteWarrantyColumns(data),
          ...buildQuoteHeadingColumns(data),
          ...buildQuoteExtraFieldsColumns(data),
          notes: data.notes || null,
          terms_conditions: data.termsConditions || null,
          ...buildQuoteTotalsColumns({ ...data, items }),
          ...buildQuotePaymentColumns(data),
          ...buildQuotePdfTemplateColumns(data),
          title: data.title?.trim() || null,
          show_sequence_id: data.showSequenceId || false,
        })
        .eq('id', id);

      if (quoteError) {
        console.error('Quote update error:', quoteError);
        throw new Error('Erro ao salvar orçamento');
      }

      // Step 1: Delete ALL existing items for this quote first
      const { error: deleteAllError } = await supabase
        .from('quote_items')
        .delete()
        .eq('quote_id', id);

      if (deleteAllError) {
        console.error('Quote items delete error:', deleteAllError);
        throw new Error('Erro ao atualizar itens do orçamento');
      }

      // Step 2: Insert all current items as new (without id to let DB generate new ones)
      if (items.length > 0) {
        const itemsToInsert = items.map((item, index) => buildQuoteItemInsert(item, index, id!));

        const { error: insertError } = await supabase
          .from('quote_items')
          .insert(itemsToInsert);

        if (insertError) {
          console.error('Quote items insert error:', insertError);
          throw new Error(`Erro ao salvar itens do orçamento: ${insertError.message}`);
        }
      }

      // Update general photos - delete all and re-insert
      const generalPhotos = data.generalPhotos || [];
      
      // Delete existing photos for this quote
      await supabase.from('quote_photos').delete().eq('quote_id', id);
      
      // Insert new photos
      if (generalPhotos.length > 0 && user) {
        const photosToInsert = await Promise.all(
          generalPhotos.map(async (photo, index) => ({
            quote_id: id,
            user_id: user.id,
            image_url: await ensureStorageUrl(photo.imageUrl, 'quote-photos', 'general', user.id),
            title: photo.title || null,
            description: photo.description || null,
            position: index,
          }))
        );

        const { error: photosError } = await supabase.from('quote_photos').insert(photosToInsert);
        if (photosError) {
          console.error('Error saving general photos:', photosError);
          throw new Error(`Erro ao salvar fotos do orçamento: ${photosError.message}`);
        }
      }

      // Save items to catalog if checkbox is checked AND update quote_items with product_id
      // Also track saved product IDs to update form state
      const savedProductIds: Record<string, string> = {};
      
      if (user) {
        const itemsToSaveToCatalog = items.filter((item) => item.saveToCategory && item.name.trim());
        let catalogItemsSaved = 0;

        if (itemsToSaveToCatalog.length > 0) {
          // Porta ÚNICA (produto + fotos + variações + materiais). Ver o
          // cabeçalho de `catalogFromQuoteItem.ts`: os cinco payloads que
          // existiam aqui e no NewQuotePage tinham listas de colunas
          // DIFERENTES, e o usuário perdia o que tinha acabado de digitar.
          for (const item of itemsToSaveToCatalog) {
            const outcome = await createCatalogProductFromQuoteItem({
              item,
              userId: user.id,
              companyId: activeCompany?.id ?? null,
            });

            if (!outcome.product) {
              console.error('Error saving item to catalog:', outcome.error);
              toast.warning(`"${item.name}" não foi salvo no catálogo: ${outcome.error}`);
              continue;
            }

            // Com variações, a linha aponta para a PRIMEIRA FILHA: o agrupador
            // não é vendável nem estocável (#30).
            await supabase
              .from('quote_items')
              .update({ product_id: outcome.product.linkProductId })
              .eq('quote_id', id)
              .eq('name', item.name)
              .eq('unit_price', item.unit_price);

            // Track for updating form state
            savedProductIds[item.id] = outcome.product.linkProductId;
            catalogItemsSaved++;

            // Cache the new catalog item for offline access
            try {
              const cachedCatalog = (getCachedCatalog(user.id) || []) as any[];
              const next = [
                {
                  id: outcome.product.productId,
                  user_id: user.id,
                  company_id: activeCompany?.id ?? null,
                  name: item.name,
                  price: item.unit_price,
                  // Espelha o que acabou de ir para o banco — inclusive as
                  // colunas de cadastro, que o cache antes descartava.
                  cost: quoteItemUnitCost(item),
                  ...catalogProductColumnsFromQuoteItem(item),
                  image_url: item.image_url || null,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  _offline_pending: false,
                },
                ...cachedCatalog.filter((p) => p?.id !== outcome.product.productId),
              ];
              cacheCatalog(user.id, next);
            } catch (e) {
              console.warn('[QuoteDetailPage] Failed to cache new catalog item:', e);
            }
          }

          if (catalogItemsSaved > 0) {
            window.dispatchEvent(new CustomEvent('catalog-updated'));
          }
        }
      }
      
      // Update form state with new product_ids and client if saved
      // This ensures items show as catalog items and client shows with green background after save
      setFormData(prev => {
        const updatedItems = prev.items.map(item => {
          if (savedProductIds[item.id]) {
            return {
              ...item,
              product_id: savedProductIds[item.id],
              source: 'catalog' as const,
              saveToCategory: false, // Clear the checkbox after saving
            };
          }
          return item;
        });
        
        let updatedClient = prev.selectedClient;
        let updatedClientName = prev.clientName;
        
        // If a new client was saved, create a proper client object for the form
        if (clientSaved && clientIdToUse) {
          updatedClient = {
            id: clientIdToUse,
            name: clientName,
            phone: clientPhone,
            address: clientAddress,
            document: clientDocument,
            user_id: user!.id,
            created_at: new Date().toISOString(),
          } as Client;
          updatedClientName = clientName;
        }
        
        return {
          ...prev,
          items: updatedItems,
          selectedClient: updatedClient,
          clientName: updatedClientName,
          saveNewClient: false, // Clear the checkbox after saving
        };
      });

      // NOTE: Editing does NOT consume credits - removed old quota logic

      // Update the initial form data reference so subsequent saves without changes don't count
      initialFormDataRef.current = JSON.stringify({
        clientName: formData.clientName,
        selectedClient: formData.selectedClient?.id || clientIdToUse || null,
        status: formData.status,
        documentType: formData.documentType,
        validityDays: formData.validityDays,
        warranty: formData.warranty,
        notes: formData.notes,
        termsConditions: formData.termsConditions,
        items: formData.items.map((item) => ({
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount,
          discount_type: item.discount_type,
        })),
      });

      if (clientSaved) {
        // O cliente novo é notícia mesmo quando o chamador tem mensagem própria:
        // é um efeito colateral do save que ele não pediu explicitamente.
        toast.success('Orçamento atualizado e cliente adicionado à lista!');
      } else if (!opts?.silent) {
        toast.success('Orçamento atualizado!');
      }

      // Clear edit draft on successful save
      clearEditDraft();

      // Update local cache so offline reopen shows items/photos immediately
      if (user) {
        const cachedQuotes = (getCachedQuotes(user.id) || []) as any[];
        const idx = cachedQuotes.findIndex((q) => q.id === id);

        const itemsForCache = items.map((item: QuoteItem, index: number) => ({
          id: item.id || `temp_${index}`,
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount,
          discount_type: item.discount_type,
          image_url: item.image_url,
          image_url_2: item.image_url_2,
          image_url_3: item.image_url_3,
          item_type: item.itemType,
          unit: item.unit,
          product_id: item.product_id,
          barcode: item.barcode,
          unit_cost: item.unit_cost ?? null,
          // Vinculo com a despesa cobravel: sem ele o proximo save vira a linha em
          // item avulso e o financeiro perde o controle dela (CLAUDE.md #70).
          finance_entry_id: item.finance_entry_id ?? null,
        }));

        const photosForCache = (generalPhotos || []).map((p: GeneralPhoto) => ({
          id: p.id,
          image_url: p.imageUrl,
          imageUrl: p.imageUrl,
          title: p.title,
          description: p.description,
        }));

        const base = idx !== -1 ? cachedQuotes[idx] : { id, user_id: user.id };
        const updated = {
          ...base,
          client_id: clientIdToUse,
          client_name: clientName,
          client_phone: clientPhone,
          client_address: clientAddress,
          client_document: clientDocument,
          status: data.status,
          document_type: data.documentType || 'orcamento',
          validity_days: parseInt(data.validityDays) || null,
          ...buildQuoteWarrantyColumns(data),
          ...buildQuoteHeadingColumns(data),
          ...buildQuoteExtraFieldsColumns(data),
          notes: data.notes || null,
          terms_conditions: data.termsConditions || null,
          // O cache precisa das MESMAS colunas: reabrir offline lê daqui, e sem
          // elas o frete sumiria da tela e o próximo save gravaria zero.
          ...buildQuoteTotalsColumns({ ...data, items }),
          ...buildQuotePaymentColumns(data),
          ...buildQuotePdfTemplateColumns(data),
          title: data.title?.trim() || null,
          show_sequence_id: data.showSequenceId || false,
          updated_at: new Date().toISOString(),
          _offline_items: itemsForCache,
          _offline_photos: photosForCache,
        };

        if (idx !== -1) {
          cachedQuotes[idx] = updated;
        } else {
          cachedQuotes.unshift(updated);
        }
        cacheQuotes(user.id, cachedQuotes);
      }

      // A gravação passou: os itens digitados viraram os itens gravados, e só
      // agora o acerto de estoque pode enxergá-los.
      setPersistedItems(data.items ?? []);
      setPersistedUpdatedAt(new Date().toISOString());

      // Dispatch event to update dashboard
      window.dispatchEvent(new CustomEvent('quotes-updated'));
      return true;
    } catch (e) {
      console.error('Save quote (edit) error:', e);
      toast.error(e instanceof Error ? e.message : 'Erro ao salvar orçamento');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!canDelete) {
      toast.warning('Apenas o proprietário e administradores podem excluir.');
      return;
    }

    // Offline delete support
    if (!isOnline && user) {
      // If it's a pending offline-created quote, remove it from the queue instead of syncing a delete
      if (String(id).startsWith('offline_')) {
        removeFromOfflineQueue(id);
      } else {
        // A devolução NÃO é calculada aqui: sem rede não dá para ler os
        // movimentos do servidor, e o cache local pode estar velho. O que viaja
        // na fila é a DECISÃO; o executor recalcula e aplica no sync, com o
        // documento ainda vivo no Postgres (ver stockDocumentReturn.ts).
        addToOfflineQueue('delete_quote', {
          quoteId: id,
          userId: user.id,
          companyId: activeCompany?.id ?? null,
          returnStock: deletionReturn.intent,
        });
      }

      const cachedQuotes = (getCachedQuotes(user.id) || []) as any[];
      cacheQuotes(user.id, cachedQuotes.filter((q) => q.id !== id));

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('quotes-updated'));
      toast.success('Orçamento excluído localmente! Será sincronizado quando houver internet.', {
        icon: '📴',
      });
      navigate('/');
      return;
    }
    
    setDeleting(true);

    // Calcular → apagar → devolver, nessa ordem e num lugar só. Devolver ANTES
    // do delete (o que o app fazia) inflava o estoque de um orçamento que
    // continuava vivo quando o delete falhava; e os filhos NÃO são apagados
    // aqui — são CASCADE, e apagá-los antes era o que deixava o fantasma
    // "aprovado, total cheio, zero itens". Ver `src/lib/quoteDeletion.ts`.
    let outcome: DocumentDeletionOutcome<QuoteDeletionCounts>;
    try {
      outcome = await runDocumentDeletion({
        prepareReturn: () => deletionReturn.prepare(),
        remove: () => deleteQuotesCascade([id]),
      });
    } catch (e) {
      console.error('Delete quote error:', e);
      toast.error('Erro ao excluir orçamento');
      return;
    } finally {
      setDeleting(false);
    }

    if (outcome.prepareFailed || outcome.returnFailed) {
      toast.warning('O orçamento foi excluído, mas não foi possível devolver o estoque.');
    } else if (outcome.returned) {
      toast.success(
        `${plural(outcome.returned.products, 'produto devolvido', 'produtos devolvidos')} ao estoque.`,
      );
    }

    const releaseMessage = billableReleaseMessage(outcome.result.released);
    if (releaseMessage) toast.info(releaseMessage);

    toast.success('Orçamento excluído.');
    window.dispatchEvent(new CustomEvent('quotes-updated'));
    navigate('/');
  };

  // Handle client change request - show confirmation modal only for free users
  const handleClientChangeRequest = (callback: () => void) => {
    // PRO users don't need confirmation - proceed directly
    if (unlimitedQuotes.allowed || formDataRef.current.documentType === 'pedido') {
      callback();
      return;
    }
    
    // Check if free user can create a new quote (for credit consumption)
    if (!canCreateQuote) {
      unlimitedQuotes.require();
      toast.warning(`Você atingiu o limite deste mês (${freeQuotesLimit} orçamentos). A cota renova todo dia 1º.`);
      return;
    }
    
    setPendingClientChangeCallback(() => callback);
    setShowClientChangeModal(true);
  };

  // Handle confirmed client change - proceeds with client selector
  const handleConfirmClientChange = () => {
    setShowClientChangeModal(false);
    if (pendingClientChangeCallback) {
      pendingClientChangeCallback();
      setPendingClientChangeCallback(null);
    }
  };

  /**
   * Salva antes de derivar. Sem isto o documento novo nasce de um orçamento
   * desatualizado — era o preâmbulo repetido em cada botão do antigo card.
   */
  const saveBeforeDerive = async (what: string) => {
    try {
      setSaving(true);
      await handleSave();
    } catch (error) {
      console.error(`Error saving before ${what}:`, error);
    } finally {
      setSaving(false);
    }
  };

  /**
   * O que ESTA tela sabe fazer. A trilha escolhe qual delas vira a ação
   * primária (a sugerida) e recolhe o resto — nenhuma some, e o rótulo passa a
   * "Gerar outro…" quando a etapa já tem documento (`workActionLabel`).
   *
   * O portão é o mesmo do card antigo: derivar exige id real no servidor (para
   * vincular e buscar itens) e permissão de escrita.
   */
  const canDerive = Boolean(id) && canWrite && !String(id).startsWith('offline_');
  const receiptReady = formData.status === 'approved' || formData.status === 'completed';
  const workActions: WorkTrailAction[] = !canDerive
    ? []
    : [
        {
          stage: 'contract',
          icon: FileSignature,
          disabled: saving || !isOnline,
          onClick: async () => {
            if (!contractsFeature.allowed) {
              contractsFeature.require();
              return;
            }
            await saveBeforeDerive('contract');
            navigate(`/contratos/novo?orcamento=${id}`);
          },
        },
        {
          stage: 'service_order',
          icon: ClipboardList,
          disabled: saving || !isOnline,
          onClick: async () => {
            await saveBeforeDerive('service order');
            navigate(`/ordens-servico/nova?orcamento=${id}`);
          },
        },
        {
          stage: 'payment',
          icon: ReceiptText,
          // Gate explícito: não some, explica o motivo (recibo é grátis).
          disabled: !receiptReady,
          hint: 'Aprove o orçamento para gerar o recibo do pagamento.',
          onClick: () =>
            navigate('/recibos/novo', {
              state: {
                prefill: {
                  quote_id: id,
                  client_id: formData.selectedClient?.id ?? null,
                  // clientName é a fonte confiável (snapshot do orçamento OU
                  // digitado); selectedClient pode ser null em orçamentos sem
                  // cliente vinculado.
                  payer_name: formData.clientName || formData.selectedClient?.name || null,
                  payer_document:
                    formData.newClientDocument ||
                    (formData.selectedClient as any)?.document ||
                    null,
                  payer_address:
                    formData.newClientAddress || formData.selectedClient?.address || null,
                  amount: calculateTotal(formData),
                  description: formData.title || null,
                },
              } satisfies ReceiptRouteState,
            }),
        },
        ...(nfseFeature.allowed
          ? [
              {
                stage: 'nfse' as const,
                icon: ReceiptText,
                disabled: saving || !isOnline,
                onClick: async () => {
                  await saveBeforeDerive('NFS-e');
                  navigate(`/nfse/nova?quote=${id}`);
                },
              },
            ]
          : []),
      ];

  // Create a new quote with the changed client (called when saving after client change)
  const handleSaveWithClientChange = async () => {
    if (!user || !id) return;
    if (!canWrite) {
      toast.warning('Você tem acesso somente leitura nesta empresa.', { id: 'readonly-access' });
      return;
    }

    const currentClientId = formData.selectedClient?.id || null;
    const clientChanged = originalClientId !== null && currentClientId !== originalClientId;
    
    if (!clientChanged) {
      // No client change - proceed with normal save
      await handleSave();
      return;
    }
    
    // Client was changed - create a new quote instead of updating
    const countTowardQuota = formDataRef.current.documentType !== 'pedido';
    if (countTowardQuota && !unlimitedQuotes.allowed && !canCreateQuote) {
      unlimitedQuotes.require();
      toast.warning(`Você atingiu o limite deste mês (${freeQuotesLimit} orçamentos). A cota renova todo dia 1º.`);
      return;
    }
    
    setSaving(true);
    
    try {
      const data = formDataRef.current;
      const clientName = data.clientName.trim();
      const clientPhone = data.newClientPhone?.trim() || null;
      const clientAddress = data.newClientAddress?.trim() || null;
      const clientDocument = onlyDigits(data.selectedClient?.document || data.newClientDocument || '') || null;
      const items: QuoteItem[] = Array.isArray(data.items) ? data.items : [];

      // Get next sequence ID
      const { data: profileData } = await supabase
        .from('profiles')
        .select('lifetime_quotes_created')
        .eq('id', user.id)
        .maybeSingle();
      
      const currentCount = profileData?.lifetime_quotes_created ?? 0;
      const nextSequenceId = currentCount + 1;
      
      // Get client ID to use
      let clientIdToUse = data.selectedClient?.id || null;
      
      // Create new quote
      const { data: newQuote, error: quoteError } = await supabase
        .from('quotes')
        .insert({
          user_id: user.id,
          client_id: clientIdToUse,
          client_name: clientName,
          client_phone: clientPhone,
          client_address: clientAddress,
          client_document: clientDocument,
          company_id: quoteCompany?.id || activeCompany?.id || null,
          status: data.status,
          document_type: data.documentType || 'orcamento',
          validity_days: parseInt(data.validityDays) || null,
          ...buildQuoteWarrantyColumns(data),
          ...buildQuoteHeadingColumns(data),
          ...buildQuoteExtraFieldsColumns(data),
          notes: data.notes || null,
          terms_conditions: data.termsConditions || null,
          ...buildQuoteTotalsColumns({ ...data, items }),
          ...buildQuotePaymentColumns(data),
          ...buildQuotePdfTemplateColumns(data),
          title: data.title?.trim() || null,
          sequence_id: nextSequenceId,
          show_sequence_id: data.showSequenceId || false,
        })
        .select()
        .single();
      
      if (quoteError || !newQuote) {
        throw new Error('Erro ao criar novo orçamento');
      }
      
      // Insert items
      // Builder único (src/lib/quoteItems.ts): preserva product_id e o SNAPSHOT
      // de unit_cost, e corrige a conta de descontos e totais da cópia.
      const itemsToInsert = items.map((item, index) => ({
        ...buildQuoteItemInsert(item, index, newQuote.id),
        // O vínculo com a despesa cobrável NÃO é copiado de propósito: dois
        // orçamentos apontando para o mesmo lançamento fariam uma edição de
        // valor recalcular o total do documento errado. O VALOR é preservado
        // (é uma cobrança real da cópia); só o controle pelo financeiro fica
        // com o orçamento original.
        finance_entry_id: null,
      }));

      if (itemsToInsert.length > 0) {
        const { error: itemsError } = await supabase.from('quote_items').insert(itemsToInsert);
        if (itemsError) {
          // O cabeçalho já existe e os itens não: sem isto sobraria na lista um
          // orçamento "com total cheio e ZERO itens", somando nos relatórios —
          // o fantasma do #109. Documento criado segundos atrás não tem despesa
          // cobrável nem baixa de estoque, então o delete direto é seguro.
          await supabase.from('quotes').delete().eq('id', newQuote.id);
          throw new Error(`Erro ao salvar itens do orçamento: ${itemsError.message}`);
        }
      }

      // Increment quota for creating new quote
      await incrementQuoteCount({
        countTowardQuota: countTowardQuota && !unlimitedQuotes.allowed,
        sequenceId: nextSequenceId,
      });
      
      // Clear edit draft
      clearEditDraft();
      
      toast.success('Novo orçamento criado com sucesso!');
      window.dispatchEvent(new CustomEvent('quotes-updated'));
      
      // Navigate to the new quote
      navigate(`/quotes/${newQuote.id}`, { replace: true });
    } catch (e) {
      console.error('Error creating new quote with changed client:', e);
      toast.error(e instanceof Error ? e.message : 'Erro ao criar orçamento');
    } finally {
      setSaving(false);
    }
  };

  // Request duplicate - PRO users skip confirmation modal
  const handleDuplicateRequest = () => {
    if (!canWrite) {
      toast.warning('Você tem acesso somente leitura nesta empresa.', { id: 'readonly-access' });
      return;
    }
    // PRO users don't need confirmation - proceed directly
    if (unlimitedQuotes.allowed) {
      handleDuplicate();
      return;
    }
    
    // Check quota for free users
    if (formData.documentType !== 'pedido' && !canCreateQuote) {
      unlimitedQuotes.require();
      return;
    }
    // Show confirmation modal for free users
    setShowDuplicateModal(true);
  };

  const handleDuplicate = async () => {
    if (!id || !user) return;
    
    setShowDuplicateModal(false);
    setDuplicating(true);

    // Get current lifetime_quotes_created from profile for persistent sequence ID
    const { data: profileData } = await supabase
      .from('profiles')
      .select('lifetime_quotes_created')
      .eq('id', user.id)
      .maybeSingle();

    // Next sequence ID = lifetime_quotes_created + 1 (before incrementing)
    const currentCount = profileData?.lifetime_quotes_created ?? 0;
    const nextSequenceId = currentCount + 1;
    
    // Create new quote with same data
    const { data: newQuote, error: quoteError } = await supabase
      .from('quotes')
      .insert({
        user_id: user.id,
        client_id: formData.selectedClient?.id || null,
        client_name: formData.clientName,
        status: 'pending',
        validity_days: parseInt(formData.validityDays) || null,
        ...buildQuoteWarrantyColumns(formData),
        ...buildQuoteHeadingColumns(formData),
        ...buildQuoteExtraFieldsColumns(formData),
        notes: formData.notes || null,
        ...buildQuoteTotalsColumns(formData),
        ...buildQuotePaymentColumns(formData),
        ...buildQuotePdfTemplateColumns(formData),
        document_type: formData.documentType || 'orcamento',
        terms_conditions: formData.termsConditions || null,
        title: formData.title ? `${formData.title} (Cópia)` : null,
        sequence_id: nextSequenceId,
        show_sequence_id: formData.showSequenceId || false,
        company_id: quoteCompany?.id || activeCompany?.id || null,
      })
      .select()
      .single();
    
    if (quoteError || !newQuote) {
      setDuplicating(false);
      toast.error('Erro ao duplicar orçamento');
      return;
    }
    
    // Copy items to new quote
    // Builder único (src/lib/quoteItems.ts): preserva product_id e o SNAPSHOT
    // de unit_cost, e corrige a conta de descontos e totais da cópia.
    const itemsToInsert = formData.items.map((item, index) => ({
      ...buildQuoteItemInsert(item, index, newQuote.id),
      // Mesma razão do "salvar como novo": a cópia herda a COBRANÇA, não o
      // vínculo com o lançamento do financeiro.
      finance_entry_id: null,
    }));

    const { error: itemsError } = await supabase.from('quote_items').insert(itemsToInsert);
    if (itemsError) {
      // Mesma razão do "salvar como novo": a cópia sem itens seria um documento
      // fantasma na lista (#109), e ela acabou de nascer — nada aponta para ela.
      console.error('Duplicate quote items error:', itemsError);
      await supabase.from('quotes').delete().eq('id', newQuote.id);
      setDuplicating(false);
      toast.error('Erro ao duplicar os itens do orçamento. Nada foi criado.');
      return;
    }

    setDuplicating(false);
    toast.success('Orçamento duplicado!');
    window.dispatchEvent(new CustomEvent('quotes-updated'));
    // Increment lifetime count in database (historical consumption)
    await incrementQuoteCount({
      countTowardQuota: formData.documentType !== 'pedido' && !unlimitedQuotes.allowed,
      sequenceId: nextSequenceId,
    });
    
    // Navigate to the new quote
    navigate(`/quotes/${newQuote.id}`);
  };

  if (loading) {
    return (
      <MainLayout showFab={false}>
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10" />
            <Skeleton className="h-8 w-48" />
          </div>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </MainLayout>
    );
  }

  if (notFound) {
    return (
      <MainLayout showFab={false}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h1 className="text-2xl font-bold text-muted-foreground">Orçamento não encontrado</h1>
          <p className="mt-2 text-muted-foreground">
            O orçamento solicitado não existe ou você não tem permissão para acessá-lo.
          </p>
          <Button3D className="mt-6" onClick={() => navigate('/')}>
            Voltar ao início
          </Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showFab={false}>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o mesmo cabeçalho sangrado de /quotes/new                    */}
        {/* ------------------------------------------------------------------ */}
        {/* Número, switch do PDF e status vivem aqui (e por isso o formulário
            recebe `showIdentityFields={false}`): o cabeçalho é o lugar onde o
            prestador confere de relance o que está editando e por quanto. */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
              onClick={() => navigate(-1)}
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">
                {canWrite ? 'Editando' : 'Somente leitura'}
              </p>
              <h1 className="truncate text-lg font-bold leading-tight">
                {getDocumentTypeShortLabel(formData.documentType)}
              </h1>
            </div>

            {canWrite && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
                onClick={handleDuplicateRequest}
                disabled={duplicating}
                aria-label="Duplicar orçamento"
                title="Duplicar orçamento"
              >
                {duplicating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            )}

            {canDelete && (
              <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-red-500/80 hover:text-white"
                    aria-label="Excluir orçamento"
                    title="Excluir orçamento"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir orçamento?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Tem certeza que deseja excluir este orçamento? Essa ação não pode ser desfeita.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <StockReturnOnDeleteNotice state={deletionReturn} />
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      disabled={deleting}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Excluindo...
                        </>
                      ) : (
                        'Excluir'
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

          </div>

          <div className="relative mt-5 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-white/75">
                Total {documentTypeOfLabel(formData.documentType)}
              </p>
              <p
                className={cn(
                  'truncate font-bold leading-tight',
                  heroTotalSizeClass(formatCurrency(total)),
                )}
              >
                {formatCurrency(total)}
              </p>
              <p className="mt-0.5 text-xs text-white/70">
                {formData.items.length === 0
                  ? 'Nenhum item ainda'
                  : plural(formData.items.length, 'item', 'itens')}
              </p>
            </div>

            {/* Número + "Nº no PDF" na MESMA coluna. Na tela de criação o chip do
                número fica na linha de cima, mas lá o cabeçalho tem um botão a
                menos: aqui, com duplicar E excluir, sobravam 117px para o título
                e "Proposta comercial" precisa de 162 — sairia cortado (medido em
                360px; é a armadilha #52). Descendo o chip, o título volta a caber
                inteiro e o número fica ao lado do interruptor que fala dele. */}
            {!!formData.sequenceId && (
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                <div className="flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1">
                  <Hash className="h-3.5 w-3.5 text-white/80" />
                  <span className="font-mono text-sm font-bold leading-none">
                    {String(formData.sequenceId).padStart(4, '0')}
                  </span>
                </div>
                {canWrite && (
                  /* Trilho ESCURO de propósito: o thumb do Switch é branco, e um
                     trilho claro sobre o hero verde deixaria o controle invisível. */
                  <label className="flex cursor-pointer items-center gap-2 rounded-full bg-emerald-950/25 px-3 py-1.5">
                    <span className="text-[11px] leading-none text-white">Nº no PDF</span>
                    <Switch
                      checked={formData.showSequenceId || false}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, showSequenceId: checked }))
                      }
                      className="scale-90 data-[state=checked]:bg-emerald-900 data-[state=unchecked]:bg-emerald-950/60"
                    />
                  </label>
                )}
              </div>
            )}
          </div>

          {/* Status: os 4 selecionáveis cabem na largura da tela — grade de 4
              colunas, não trilho rolável (que escondia "Cancelado" na borda).
              `rejected` NÃO está entre eles porque quem recusa é o cliente, pela
              página pública (ver `quoteStatus.ts`); quando o documento está nesse
              estado, ele aparece como selo à parte para não sumir da tela — era
              o que o seletor antigo já fazia ao exibir um valor fora da lista. */}
          <div className="mt-4 grid grid-cols-4 gap-1.5">
            {QUOTE_STATUS_OPTIONS.map((option) => {
              const active = option.value === formData.status;
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={!canWrite}
                  onClick={() => setFormData((prev) => ({ ...prev, status: option.value }))}
                  className={cn(
                    'flex min-w-0 items-center justify-center gap-1 rounded-full px-1 py-1.5 text-[11px] font-medium transition-all',
                    active
                      ? 'bg-white text-emerald-800 shadow-[inset_0_-3px_0_rgba(0,0,0,0.12)]'
                      : 'bg-white/15 text-white/85 hover:bg-white/25',
                    !canWrite && 'cursor-default',
                  )}
                >
                  <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', option.dot)} />
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })}
          </div>

          {formData.status === 'rejected' && (
            <div className="mt-2 flex items-center justify-center gap-1.5 rounded-full bg-emerald-950/25 px-3 py-1.5 text-[11px] font-medium">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-status-cancelled" />
              {QUOTE_STATUS_LABELS.rejected} pelo cliente
            </div>
          )}
        </div>

        {/* Conteúdo — o primeiro cartão sobe e encosta na curva do hero */}
        <div className="relative z-10 -mt-9 space-y-5">

        {/* Dar baixa no estoque — só com o orçamento CONCLUÍDO. Aprovado é o
            aceite do cliente, não a entrega: baixar ali reserva mercadoria por
            um serviço que ainda pode não acontecer (ver `stockSettings.ts`).
            Só produtos rastreados; recurso PRO. */}
        {id &&
          canWrite &&
          !String(id).startsWith('offline_') &&
          formData.status === 'completed' && (
            <StockDeductionPrompt items={formData.items as any} quoteId={id} />
          )}

        {/* Devolver ao estoque quando o orçamento é cancelado/rejeitado. Nasce
            DESLIGADO nas configurações (comportamento novo) e a quantidade vem
            dos MOVIMENTOS, não do documento — ver `src/lib/stockReturn.ts`. */}
        {id && canWrite && !String(id).startsWith('offline_') && (
          <StockReturnPrompt quoteId={id} status={formData.status} />
        )}

        {/* Acertar o estoque quando a quantidade muda DEPOIS da baixa. Age só
            sobre produtos que ainda estão baixados por este documento — item
            novo continua sendo trabalho do prompt de baixa acima, e os dois
            conjuntos são complementares por construção (ver stockReturn.ts). */}
        {id && canWrite && !String(id).startsWith('offline_') && (
          <StockAdjustmentPrompt
            items={persistedItems as any}
            documentUpdatedAt={persistedUpdatedAt}
            quoteId={id}
          />
        )}

        {/* Despesas cobráveis — gastos repassados ao cliente. Cada uma vira uma
            LINHA deste orçamento (o total é coluna armazenada, então somar em
            tempo de render deixaria o banco desatualizado). O orçamento é o
            dono: por isso o ponto de entrada é aqui, e não em /financeiro. */}
        {id && canWrite && (
          <QuoteBillableExpenses quoteId={id} quoteStatus={formData.status} />
        )}

        {/* Cobrança Pix — só quando a detecção automática está valendo, ou
            quando já existe cobrança (desligar o interruptor não desfaz uma
            cobrança já enviada ao cliente). O card se esconde sozinho. */}
        {id && (
          <QuotePixChargeCard
            quoteId={id}
            total={calculateTotal(formData)}
            title={formData.title}
            sequenceId={formData.sequenceId}
            clientId={formData.selectedClient?.id ?? null}
            client={{
              name: formData.clientName,
              // Mesma precedência do payload do PDF: o cadastro primeiro, o
              // digitado à mão como complemento.
              document: formData.selectedClient?.document || formData.newClientDocument || null,
              email: formData.selectedClient?.email ?? null,
              phone: formData.selectedClient?.phone || formData.newClientPhone || null,
            }}
          />
        )}

        {/* Registrar recebimento no financeiro (aparece em orçamentos aprovados/concluídos).
            Finanças são restritas a owner/admin. */}
        {id && canSeeFinance && (
          <QuoteFinancePrompt
            quote={{
              id,
              total: calculateTotal(formData),
              client_id: formData.selectedClient?.id ?? null,
              title: formData.title,
              status: formData.status,
              sequence_id: null,
            }}
          />
        )}

        {/* Trilha do trabalho — estado do serviço, ação sugerida e, no accordion,
            as demais ações + os documentos gerados. Absorveu o antigo card
            "Próximos passos", que era stateless e convidava a gerar o que já
            existia. Funciona offline e com id temporário: a corrente sai do
            cache, e um orçamento recém-criado simplesmente aparece sozinho. */}
        {work && (
          <WorkTrail
            work={work}
            anchorId={id}
            actions={workActions}
            onOpenDocument={(route) => navigate(route)}
          >
            {/* Agrupar este orçamento com outro trabalho (Fase 3). Fica no
                accordion porque agrupar é exceção: a corrente derivada resolve a
                esmagadora maioria dos casos. */}
            <WorkGroupSection roots={workRoots} onChanged={refetchWork} />

            {/* #7 — o layout do orçamento imita uma OS, mas continua sendo um
                orçamento (o tipo é só rótulo no PDF). Direciona para a entidade real. */}
            {formData.documentType === 'ordem_servico' && (
              <div className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  Este documento usa o layout de Ordem de serviço, mas ainda é um orçamento. Para
                  acompanhar a execução (status, baixa de estoque), gere uma ordem de serviço de
                  verdade.
                </span>
              </div>
            )}
          </WorkTrail>
        )}

        {/* Client Change Confirmation Modal */}
        <AlertDialog open={showClientChangeModal} onOpenChange={setShowClientChangeModal}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Atenção
              </AlertDialogTitle>
              <AlertDialogDescription className="text-base">
                Trocar o cliente criará um <strong>novo orçamento</strong> e consumirá <strong>1 crédito</strong> da sua conta.
                <br /><br />
                O orçamento original permanecerá inalterado.
                <br /><br />
                <span className="text-muted-foreground">
                  (Você tem {remainingQuotes} {remainingQuotes === 1 ? 'crédito restante' : 'créditos restantes'} neste mês)
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setPendingClientChangeCallback(null)}>
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmClientChange}>
                Continuar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Duplicate Confirmation Modal */}
        <AlertDialog open={showDuplicateModal} onOpenChange={setShowDuplicateModal}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Duplicar orçamento?
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>
                  Essa ação irá consumir <strong>1 crédito</strong> da sua conta.
                </p>
                {!unlimitedQuotes.allowed && (
                  <p className="text-sm text-muted-foreground">
                    Você possui {remainingQuotes} crédito{remainingQuotes !== 1 ? 's' : ''} restante{remainingQuotes !== 1 ? 's' : ''} neste mês. A cota renova todo dia 1º.
                  </p>
                )}
                <p>Deseja continuar?</p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDuplicate}>
                Duplicar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>


        {/* OS e Contrato saíram daqui: viraram ROTAS (`/ordens-servico/nova` e
            `/contratos/novo`, com `?orcamento=` semeando a origem). Ao voltar,
            a trilha se atualiza sozinha — `useWork` tem `refetchOnMount:
            'always'`, e voltar remonta esta página. */}

        {/* Envio da mensagem ao cliente (modelo + variáveis + revisão) */}
        <SendMessageDialog
          open={sendMessageOpen}
          onOpenChange={setSendMessageOpen}
          category="quote"
          context={messageContext}
          phone={clientPhoneForMessage}
          title="Enviar orçamento"
        />

        {/* Formulário — numeração e status ficam no HERO, não aqui dentro (sem
            `showIdentityFields={false}` os mesmos controles apareceriam duas
            vezes na tela). */}
        <QuoteForm
          data={formData}
          onChange={setFormData}
          disabled={false}
          showSaveToCatalog={true}
          showTypeAndUnit={true}
          isExistingQuote={true}
          showIdentityFields={false}
          onClientChangeRequest={handleClientChangeRequest}
        />

        {/* Assinatura de aprovação (quando o cliente aprovou pela página pública) */}
        {id && (
          <QuoteApprovalSignature
            quoteId={id}
            approved={formData.status === 'approved' || formData.status === 'completed'}
          />
        )}

        {/* Offline Warning */}
        {!isOnline && (
          <Alert className="border-amber-500/50 bg-amber-500/10">
            <WifiOff className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-700 text-sm">
              Sem internet: Link e Visualização indisponíveis. Você pode salvar o orçamento e baixar
              o PDF offline.
            </AlertDescription>
          </Alert>
        )}

        {/* Balcão que orça na hora (oficina, assistência técnica): o cliente
            está esperando. Passa pelo MESMO salvar-e-gerar do "Baixar PDF".
            Fica FORA da barra flutuante de propósito: é ação de nicho (PRO) e
            uma quarta linha colada na navegação comeria a tela. */}
        <ThermalPrintButton onPrint={(format) => handleDownloadPDF(format)} />
        </div>

        {/*
          Barra FLUTUANTE acima da navegação inferior — mesma da tela de criação:
          com o formulário longo, o botão de salvar ficava a três rolagens de
          distância do que o usuário acabou de digitar.

          Ela precisa ser filha DIRETA do container da página. `sticky` só desliza
          dentro do próprio pai — dentro de um wrapper que só contivesse ela, o
          pai teria a altura da barra e não sobraria percurso.
        */}
        {/*
          UMA linha (ver QuoteActionBar): Salvar + Enviar + "⋯".

          "Enviar por WhatsApp" fica VISÍVEL, e não no menu, porque num orçamento
          que já existe ela é a ação de negócio seguinte — é o que faz o cliente
          responder. Ela NÃO desabilita offline (diferente de link/visualização):
          os modelos ficam em cache e compor a mensagem é local, então o app
          nativo recebe o texto e enfileira o envio.

          Para o `viewer` (somente leitura) não há o que salvar: o primário passa
          a ser baixar o PDF, em vez de um botão morto.
        */}
        <QuoteActionBar
          primary={
            canWrite
              ? {
                  id: 'salvar',
                  label: saving ? 'Salvando...' : 'Salvar',
                  icon: saving ? ActionSpinner : Save,
                  onClick: () => {
                    // Check if client was changed
                    const currentClientId = formData.selectedClient?.id || null;
                    const clientChanged =
                      originalClientId !== null &&
                      currentClientId !== null &&
                      currentClientId !== originalClientId;

                    if (clientChanged) {
                      handleSaveWithClientChange();
                    } else {
                      handleSave();
                    }
                  },
                  disabled: saving,
                }
              : {
                  id: 'pdf',
                  label: saving ? 'Gerando...' : 'Baixar PDF',
                  icon: saving ? ActionSpinner : FileDown,
                  // Envolvido de propósito: passar a referência direta entregaria
                  // o `MouseEvent` do clique como parâmetro `paper`.
                  onClick: () => handleDownloadPDF(),
                  disabled: saving,
                }
          }
          secondary={{
            id: 'whatsapp',
            label: 'Enviar',
            // Logo OFICIAL do WhatsApp: a ação abre um aplicativo específico, e
            // o balão genérico do lucide lia como "mensagem" qualquer.
            icon: WhatsAppIcon,
            onClick: () => setSendMessageOpen(true),
          }}
          more={[
            ...(canWrite
              ? [
                  {
                    id: 'pdf',
                    label: saving ? 'Gerando...' : 'Baixar PDF',
                    icon: FileDown,
                    onClick: () => handleDownloadPDF(),
                    disabled: saving,
                  },
                ]
              : []),
            {
              id: 'link',
              label: 'Copiar link',
              icon: isOnline ? Link : WifiOff,
              onClick: () => {
                const publicUrl = getPublicQuoteUrl(id!);
                navigator.clipboard.writeText(publicUrl);
                toast.success('Link copiado!');
              },
              disabled: !isOnline,
              disabledReason: 'Precisa de internet',
            },
            {
              id: 'visualizar',
              label: saving ? 'Salvando...' : 'Ver como o cliente vê',
              icon: isOnline ? Eye : WifiOff,
              onClick: async () => {
                // Auto-save before viewing (only if the user can actually write;
                // viewers just open the public preview).
                if (canWrite) {
                  try {
                    setSaving(true);
                    await handleSave();
                  } catch (error) {
                    console.error('Error saving before preview:', error);
                  } finally {
                    setSaving(false);
                  }
                }
                window.open(`/o/${id}`, '_blank');
              },
              disabled: saving || !isOnline,
              disabledReason: 'Precisa de internet',
            },
          ]}
        />
      </div>
    </MainLayout>
  );
}
